import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { formatAUMobile, formatRego } from '@/lib/formatters';
import { withSessionHash } from '@/lib/authFetch';
import { usePageZoom } from '@/lib/pageZoom';
import PageZoomControls from '@/components/PageZoomControls';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Plus, 
  Trash2, 
  GripVertical,
  ArrowLeft,
  Printer,
  Mail,
  MessageSquare,
  Send,
  Save,
  X,
  Search,
  FileText,
  Wrench,
  Package,
  Clock,
  Settings2,
  Eye,
  EyeOff,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  Truck,
  Phone,
  Car,
  Crown,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSite } from '@/context/SiteContext';
import { useCommsGate } from '@/components/CommsUpgradeDialog';
import ServiceNotesModal from '@/components/invoices/ServiceNotesModal';
import AddSupplierDialog from '@/components/invoices/AddSupplierDialog';
import QuickBookingModal from '@/components/QuickBookingModal';
import { computeRowPricing, computeTemplateLineTotal, orpToRrp as orpToRrpHelper } from '@/lib/pricing';
import SupplierCombobox from '@/components/invoices/SupplierCombobox';
import { UNIT_OPTIONS } from '@/lib/units';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700' },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700' },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700' },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-500' }
};

const generateLineId = () => `line_${Math.random().toString(36).substr(2, 8)}`;

/**
 * SortableInvoiceRow — wraps each invoice line item so the SM can grab
 * the 6-dot GripVertical handle and drag the row to a new position.
 * Uses @dnd-kit so the same UX works for desktop mouse AND iPad touch
 * (PointerSensor handles mouse, TouchSensor handles iPad with a short
 * long-press to differentiate from a scroll gesture).
 *
 * Renders its children as a function so the parent can decide where
 * to attach the drag handle (the GripVertical sits in the FIRST cell
 * of the FIRST TableRow, NOT on the expanded panel). Applies the
 * transform style to the row's TableRow via setNodeRef.
 */
function SortableInvoiceRow({ id, children }) {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    // Lift the dragged row above sibling rows so the move is visible.
    position: isDragging ? 'relative' : undefined,
    zIndex: isDragging ? 10 : undefined,
    background: isDragging ? '#f1f5f9' : undefined,
  };
  return children({ setNodeRef, attributes, listeners, style, isDragging });
}

/**
 * SERVICE_CATEGORIES — workshop's internal taxonomy for the "Main Category"
 * multi-select on each invoice. Used by Super Admin for reconciliation and
 * reporting. NEVER printed on customer-facing PDFs.
 * Values mirror backend invoices_models.py:ServiceCategory enum.
 */
const SERVICE_CATEGORIES = [
  { value: 'general_service',  label: 'Service' },
  { value: 'rwc',              label: 'RWC' },
  { value: 'tyres',            label: 'Tyres' },
  { value: 'brakes',           label: 'Brakes' },
  { value: 'suspension',       label: 'Suspension' },
  { value: 'aircon',           label: 'A/C' },
  { value: 'electrical',       label: 'Electrical' },
  { value: 'transmission',     label: 'Transmission' },
  { value: 'clutch',           label: 'Clutch' },
  { value: 'timing_belt',      label: 'Timing Belt' },
  { value: 'cooling_system',   label: 'Cooling' },
  { value: 'exhaust',          label: 'Exhaust' },
  { value: 'battery',          label: 'Battery' },
  { value: 'diagnostic',       label: 'Diagnostic' },
  { value: 'other',            label: 'Other' },
];

/**
 * ServiceCategoriesPicker — chip-style multi-select for invoice.service_categories.
 * iter138.9h — visible in COL 4 of the Information section. Lets SM tag
 * the invoice with what was actually worked on (Service / RWC / Tyres /
 * Brakes / etc.) so Super Admin's reports filter correctly.
 */
function ServiceCategoriesPicker({ value, onChange }) {
  const set = new Set(value || []);
  const toggle = (v) => {
    const next = new Set(set);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange(Array.from(next));
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="invoice-categories-picker">
      {SERVICE_CATEGORIES.map(opt => {
        const active = set.has(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className={`px-2 py-1 text-[11px] font-medium rounded-md border transition-colors touch-manipulation ${
              active
                ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700'
                : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-100'
            }`}
            data-testid={`invoice-category-${opt.value}${active ? '-active' : ''}`}
            aria-pressed={active}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * AccountTypeBadge — surfaces the customer's credit-account status in
 * COL 4 of the Information section. Shows "Net <days> account" pill when
 * the customer is on account, else "N/A". Overdue shows red.
 */
function AccountTypeBadge({ accountInfo }) {
  if (!accountInfo || !accountInfo.has_account) {
    return (
      <div
        className="h-9 rounded-md border border-slate-200 bg-slate-50 px-3 flex items-center text-sm text-slate-500"
        data-testid="invoice-account-type"
      >
        N/A — Walk-in / Cash
      </div>
    );
  }
  const days = accountInfo.credit_terms_days || 30;
  const overdue = accountInfo.is_overdue;
  return (
    <div
      className={`h-9 rounded-md border px-3 flex items-center justify-between text-sm font-medium ${
        overdue ? 'border-rose-300 bg-rose-50 text-rose-800' : 'border-emerald-300 bg-emerald-50 text-emerald-800'
      }`}
      data-testid="invoice-account-type"
      title={accountInfo.account_name || 'Credit account'}
    >
      <span className="truncate">{accountInfo.account_name || 'Credit account'}</span>
      <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-white/80">
        {overdue ? `${days}d · OVERDUE` : `Net ${days}`}
      </span>
    </div>
  );
}

export default function InvoiceEdit() {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const { getSiteParams } = useSite();
  const { guardComms, commsDialog } = useCommsGate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const isNewInvoice = !invoiceId || invoiceId === 'new';

  // iter148 — Per-page zoom that ONLY affects Invoice edit page. Setting
  // persists per-browser via localStorage under `pageZoom.invoice`. Other
  // pages are unaffected.
  const { scale: pageScale, zoomIn, zoomOut, reset: resetZoom, MIN_SCALE, MAX_SCALE } = usePageZoom('invoice');
  
  // Invoice state
  const [invoice, setInvoice] = useState({
    doc_no: '',
    order_no: '',
    customer_id: '',
    customer_name: '',
    vehicle_id: '',
    vehicle_rego: '',
    vin_number: '',
    transmission: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    service_date: '',
    next_service_date: '',
    service_interval: '',
    service_categories: [],
    odometer_reading: '',
    rego_expiry: '',
    status: 'draft',
    gst_inclusive: true,
    gst_rate: 10,
    discount_type: 'dollar',
    discount_value: 0,
    amount_paid: 0,
    notes: '',
    line_items: [],
    quote_items: [],
    notes_items: []
  });
  
  // Calculated totals (updated whenever line_items or discount changes)
  const [totals, setTotals] = useState({
    subtotal: 0,
    discount_total: 0,
    gst_total: 0,
    total: 0,
    amount_due: 0
  });

  // Workshop labour rate fetched from workshop_settings (default $125.50)
  const [workshopLabourRate, setWorkshopLabourRate] = useState(125.5);
  const [workshopDefaultMargin, setWorkshopDefaultMargin] = useState(40);
  const [workshopOrpMargin, setWorkshopOrpMargin] = useState(30);
  const [showServiceNotesModal, setShowServiceNotesModal] = useState(false);
  // iter138.9ao — Book Customer from Quote Builder
  const [bookingFromQuote, setBookingFromQuote] = useState(null);  // {prefill, prefillPendingItems} | null
  // Cost-breakdown visibility — internal-only, defaults to true for super_admin / service_manager
  const [showCostBreakdown, setShowCostBreakdown] = useState(true);
  // Per-line expanded state. Each line ID in this Set has its structured
  // breakdown panel open (Category / ORP / Supplier / RRP / Margin / Labour).
  // Default = collapsed → the table looks like the printable PDF. Lines
  // with margin issues / missing fields auto-expand below.
  const [expandedLineIds, setExpandedLineIds] = useState(() => new Set());
  const toggleLineExpand = (lineId) => {
    setExpandedLineIds(prev => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };
  const expandAllLines = () => {
    setExpandedLineIds(new Set((invoice?.line_items || []).map(l => l.line_id)));
  };
  const collapseAllLines = () => setExpandedLineIds(new Set());
  // ORP (supplier cost) is workshop-internal. We default to VISIBLE so the
  // SM/SA can immediately see what was paid the supplier per line — this is
  // critical because daily ORP figures are aggregated from approved invoice
  // line items (not quotes). The eye-icon toggle lets the SM hide ORP when
  // a customer is standing at the counter to keep margin info private.
  const [showOrp, setShowOrp] = useState(true);
  // Credit-account info for the selected customer (null if none)
  const [accountInfo, setAccountInfo] = useState(null);
  // iter138.9ae — locks the On-Account pill while its PATCH is in flight
  // so a double-click doesn't fire two opposite requests.
  const [togglingAccount, setTogglingAccount] = useState(false);
  
  // Customer search
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [customerVehicles, setCustomerVehicles] = useState([]);

  // Edit Customer Details modal — lets the SM update mobile/email/address
  // for the customer linked to this invoice. Updates flow back to the
  // customer DB AND the invoice snapshot so the printed PDF reflects the
  // latest details.
  const [showEditCustomer, setShowEditCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    display_name: '',
    first_name: '',
    last_name: '',
    email: '',
    mobile: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    suburb: '',
    state: '',
    postcode: '',
    abn: '',
    company: '',
  });
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [loadingCustomerEdit, setLoadingCustomerEdit] = useState(false);

  // Edit Vehicle Details modal — lets the SM update rego/make/model/year/
  // VIN/colour/variant/odometer/rego_expiration on the customer's vehicle
  // record. Mirrors the Edit Customer flow: updates write to /api/vehicles/{id}
  // AND refresh the invoice snapshot (vehicle_rego, vehicle_make, vehicle_model,
  // vehicle_year, vin_number) so the PDF reflects the latest.
  const [showEditVehicle, setShowEditVehicle] = useState(false);
  const [vehicleForm, setVehicleForm] = useState({
    rego: '', make: '', model: '', year: '', variant: '',
    vin: '', color: '', odometer: '',
  });
  const [savingVehicle, setSavingVehicle] = useState(false);
  const [loadingVehicleEdit, setLoadingVehicleEdit] = useState(false);
  
  // Parts catalog
  const [showPartsDialog, setShowPartsDialog] = useState(false);
  const [partsCatalog, setPartsCatalog] = useState([]);
  const [partsSearchQuery, setPartsSearchQuery] = useState('');
  const [loadingParts, setLoadingParts] = useState(false);
  const [showCreatePartDialog, setShowCreatePartDialog] = useState(false);
  const [newPart, setNewPart] = useState({
    part_number: '',
    description: '',
    unit_price: '',
    unit: 'each',
    category: 'other'
  });
  const [savingPart, setSavingPart] = useState(false);
  
  // Templates
  const [showTemplatesDialog, setShowTemplatesDialog] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showCreateTemplateDialog, setShowCreateTemplateDialog] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    category: 'invoice_template',
    content: '',
    line_items: []
  });
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Custom Item Templates (Invoice Templates)
  const [showCustomItemsDialog, setShowCustomItemsDialog] = useState(false);
  const [invoiceTemplates, setInvoiceTemplates] = useState([]);
  const [loadingInvoiceTemplates, setLoadingInvoiceTemplates] = useState(false);
  const [customItemSearchQuery, setCustomItemSearchQuery] = useState('');

  // Add Supplier dialog — opens from the invoice line-items toolbar so SM
  // and Super Admin can register a new supplier mid-quote and have it
  // appear instantly in every SupplierCombobox dropdown.
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  
  // PDF/Email actions
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);
  
  // SMS Modal state
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [selectedSmsTemplate, setSelectedSmsTemplate] = useState('');
  const [customSmsMessage, setCustomSmsMessage] = useState('');
  // Email Modal state (iter121) — same pattern as SMS. SM can pick from
  // saved email templates (Super Admin → Templates → Email tab) or send
  // with the default branded body.
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState('');

  // iter123: SMS recipient picker — SM can pick mobile, landline, or
  // both. The Invoice SMS modal now lists both numbers (from the
  // customer record) with checkboxes. Defaults mirror what's on the
  // customer record (mobile checked if present; landline checked only
  // if no mobile is set).
  const [customer, setCustomer] = useState(null);
  // iter202 — gold member badge: membership info for the invoice's customer.
  const [memberInfo, setMemberInfo] = useState(null);
  const [smsTargets, setSmsTargets] = useState({ mobile: true, landline: false });
  // WhatsApp send state — same modal pattern would be ideal, but for v1
  // we ship a single-click "send quote PDF via WA" using the
  // "Share Recommended Repair Quote" template (kind=quote_with_pdf) that
  // gets auto-seeded on first load.
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  // "Quote SMS" mode — sends a tokenised review-link SMS via the dedicated
  // /send-quote-sms endpoint, with the invoice PDF inline on the public
  // /q/{token} page so customer can review + approve in seconds. When ON,
  // it overrides template / custom message — the backend uses its own
  // built-in template that includes the {link}.
  const [quoteSmsMode, setQuoteSmsMode] = useState(false);
  const [quoteSmsResult, setQuoteSmsResult] = useState(null);  // {link, message_preview}

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  // Fetch the per-site pricing defaults (labour rate + parts margin) to seed
  // NEW lines. Set via the "Pricing Defaults" dialog on the Invoices page.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/pricing-defaults`, {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.labour_rate) setWorkshopLabourRate(parseFloat(data.labour_rate));
          if (data?.parts_margin_pct != null) setWorkshopDefaultMargin(parseFloat(data.parts_margin_pct));
          if (data?.orp_margin_pct != null) setWorkshopOrpMargin(parseFloat(data.orp_margin_pct));
        }
      } catch (_e) { /* fall back to defaults */ }
    })();
  }, [user]);

  // Load existing invoice
  useEffect(() => {
    const fetchInvoice = async () => {
      if (isNewInvoice || !user) return;
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          // ── Normalise stale line_total / unit_price ─────────────────
          // Production saw rows where qty/unit_price/parts_price/margin
          // were edited at different times and the stored `line_total`
          // no longer matched the qty × unit_price the SM saw on screen
          // ($86.79 row + $180 row → Subtotal $304.44 instead of
          // $266.79). Run every loaded row through recomputeLine on
          // mount so the canonical totals lock in IMMEDIATELY, before
          // the SM touches anything.
          const normalisedLineItems = (data.line_items || []).map(it => {
            try {
              return recomputeLine(it, workshopLabourRate || 125.5);
            } catch (_e) {
              return it;
            }
          });
          setInvoice({
            ...data,
            invoice_date: data.invoice_date ? data.invoice_date.split('T')[0] : '',
            due_date: data.due_date ? data.due_date.split('T')[0] : '',
            service_date: data.service_date ? data.service_date.split('T')[0] : '',
            next_service_date: data.next_service_date ? data.next_service_date.split('T')[0] : '',
            rego_expiry: data.rego_expiry ? data.rego_expiry.split('T')[0] : '',
            odometer_reading: data.odometer_reading || '',
            service_categories: data.service_categories || [],
            discount_value: data.discount_value || 0,
            amount_paid: data.amount_paid || 0,
            line_items: normalisedLineItems,
            quote_items: data.quote_items || [],
            notes_items: data.notes_items || []
          });
          
          // Load customer vehicles + full customer record (for the
          // iter123 SMS multi-recipient picker which needs both
          // mobile + landline numbers from the customer doc).
          if (data.customer_id) {
            fetchCustomerVehicles(data.customer_id);
            fetchMemberInfo(data.customer_id);
            try {
              const cr = await fetch(`${BACKEND_URL}/api/customers/${data.customer_id}`, { credentials: 'include' });
              if (cr.ok) {
                const cust = await cr.json();
                setCustomer(cust);
              }
            } catch (_e) { /* noop */ }
          }
        } else {
          toast.error('Invoice not found');
          navigate('/invoices');
        }
      } catch (error) {
        console.error('Failed to fetch invoice:', error);
        toast.error('Failed to load invoice');
      }
    };
    
    if (user) {
      fetchInvoice();
    }
  }, [invoiceId, isNewInvoice, user, navigate]);

  // Pre-fill customer and vehicle from URL query params (for new invoices from VehicleDetails page)
  useEffect(() => {
    const prefillFromQueryParams = async () => {
      if (!isNewInvoice || !user) return;
      
      const customerId = searchParams.get('customerId');
      const vehicleId = searchParams.get('vehicleId');
      
      if (!customerId) return;
      
      try {
        // Fetch customer details
        const customerResponse = await fetch(`${BACKEND_URL}/api/customers/${customerId}`, {
          credentials: 'include'
        });
        
        if (customerResponse.ok) {
          const customer = await customerResponse.json();
          setInvoice(prev => ({
            ...prev,
            customer_id: customer.customer_id,
            customer_name: customer.display_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
          }));
          
          // Fetch customer's vehicles
          fetchCustomerVehicles(customer.customer_id);
          fetchMemberInfo(customer.customer_id);
          
          // If vehicleId is provided, fetch and set vehicle details
          if (vehicleId) {
            const vehicleResponse = await fetch(`${BACKEND_URL}/api/vehicles/${vehicleId}`, {
              credentials: 'include'
            });
            
            if (vehicleResponse.ok) {
              const vehicle = await vehicleResponse.json();
              setInvoice(prev => ({
                ...prev,
                vehicle_id: vehicle.vehicle_id || vehicleId,
                vehicle_rego: vehicle.rego || '',
                vin_number: vehicle.vin || prev.vin_number,
                transmission: vehicle.transmission || prev.transmission
              }));
            }
          }
        }
      } catch (error) {
        console.error('Failed to pre-fill from query params:', error);
      }
    };
    
    prefillFromQueryParams();
  }, [isNewInvoice, user, searchParams]);

  // Calculate totals whenever line items or discount changes
  useEffect(() => {
    const calculateTotals = () => {
      let subtotal = 0;
      
      // Sum the canonical per-row `item.line_total` (produced by
      // recomputeLine / computeRowPricing = qty × unit_price). Fall back to
      // qty × unit_price only for a brand-new row mid-typing before
      // line_total is populated — both formulas now agree since unit_price
      // is the full per-unit charge (parts + labour).
      invoice.line_items.forEach(item => {
        const lt = parseFloat(item.line_total);
        if (!isNaN(lt) && lt > 0) {
          subtotal += lt;
        } else {
          const qty = parseFloat(item.quantity) || 0;
          const price = parseFloat(item.unit_price) || 0;
          subtotal += qty * price;
        }
      });
      
      // Calculate discount
      let discount_total = 0;
      if (invoice.discount_type === 'percent') {
        discount_total = subtotal * (parseFloat(invoice.discount_value) || 0) / 100;
      } else {
        discount_total = parseFloat(invoice.discount_value) || 0;
      }
      
      const afterDiscount = subtotal - discount_total;
      
      // Calculate GST - always as a simple percentage of subtotal
      let gst_total = afterDiscount * (invoice.gst_rate / 100);
      let total = afterDiscount;
      
      if (!invoice.gst_inclusive) {
        // GST exclusive - add GST on top of subtotal
        total = afterDiscount + gst_total;
      }
      // If GST inclusive - total stays as afterDiscount (GST is already included in line item prices)
      
      const amount_due = total - (parseFloat(invoice.amount_paid) || 0);
      
      setTotals({
        subtotal: Math.round(subtotal * 100) / 100,
        discount_total: Math.round(discount_total * 100) / 100,
        gst_total: Math.round(gst_total * 100) / 100,
        total: Math.round(total * 100) / 100,
        amount_due: Math.round(amount_due * 100) / 100
      });
    };
    
    calculateTotals();
  }, [invoice.line_items, invoice.discount_type, invoice.discount_value, invoice.gst_inclusive, invoice.gst_rate, invoice.amount_paid]);

  // iter123: auto-default Amount Paid to the Total (incl GST). The
  // iter138.7 — Per SM request: amount_paid now defaults to $0 (was
  // auto-set to total, which kept treating every invoice as "paid on
  // the spot"). SM now ticks the "Paid" checkbox on the invoices list
  // page when payment is received, OR they manually type the amount
  // here for partial payments. Amount Due stays accurate.
  // The previous auto-default useEffect has been removed intentionally.

  const fetchMemberInfo = async (customerId) => {
    setMemberInfo(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/memberships/by-customer/${customerId}`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        if (d.is_member) setMemberInfo(d);
      }
    } catch (_e) { /* noop */ }
  };

  const fetchCustomerVehicles = async (customerId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/customers/${customerId}/vehicles`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCustomerVehicles(data.vehicles || []);
      }
    } catch (error) {
      console.error('Failed to fetch customer vehicles:', error);
    }
  };

  const searchCustomers = async (query) => {
    if (!query || query.length < 2) {
      setCustomerResults([]);
      return;
    }
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/customers?search=${encodeURIComponent(query)}&page_size=10`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setCustomerResults(data.customers || []);
      }
    } catch (error) {
      console.error('Failed to search customers:', error);
    }
  };

  // Fetch credit-account info whenever the customer changes
  useEffect(() => {
    if (!invoice.customer_id || !user) { setAccountInfo(null); return; }
    let aborted = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/accounts/by-customer/${invoice.customer_id}`, { credentials: 'include' });
        if (!r.ok) { if (!aborted) setAccountInfo(null); return; }
        const data = await r.json();
        if (!aborted) setAccountInfo(data?.has_account ? data : null);
      } catch (_e) { if (!aborted) setAccountInfo(null); }
    })();
    return () => { aborted = true; };
  }, [invoice.customer_id, user]);

  const selectCustomer = (customer) => {
    setInvoice(prev => ({
      ...prev,
      customer_id: customer.customer_id,
      customer_name: customer.display_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
    }));
    fetchCustomerVehicles(customer.customer_id);
    setShowCustomerSearch(false);
    setCustomerSearchQuery('');
    setCustomerResults([]);
  };

  // Open the "Edit Customer Details" modal — fetches the latest customer
  // record so the SM is editing the real data (not the invoice snapshot,
  // which may be stale if the SM has been editing the invoice for a while).
  const openEditCustomer = async () => {
    if (!invoice.customer_id) {
      toast.error('Pick a customer first');
      return;
    }
    setShowEditCustomer(true);
    setLoadingCustomerEdit(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/customers/${invoice.customer_id}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const c = await res.json();
        setCustomerForm({
          display_name: c.display_name || '',
          first_name: c.first_name || '',
          last_name: c.last_name || '',
          email: c.email || '',
          mobile: c.mobile || '',
          phone: c.phone || '',
          address_line1: c.address_line1 || c.address || '',
          address_line2: c.address_line2 || '',
          suburb: c.suburb || '',
          state: c.state || '',
          postcode: c.postcode || '',
          abn: c.abn || '',
          company: c.company || '',
        });
      } else {
        toast.error('Failed to load customer details');
        setShowEditCustomer(false);
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load customer details');
      setShowEditCustomer(false);
    } finally {
      setLoadingCustomerEdit(false);
    }
  };

  // Save the edited customer back to the customer DB, then refresh the
  // invoice's denormalised snapshot (customer_name / phone / email) so
  // the printed PDF + invoice header reflect the latest details
  // immediately. Works for both newly-promoted (lead → customer) and
  // existing customers.
  const saveCustomerDetails = async () => {
    if (!invoice.customer_id) return;
    // Auto-fill display_name from first/last if SM left it empty.
    const displayName = customerForm.display_name?.trim()
      || `${customerForm.first_name || ''} ${customerForm.last_name || ''}`.trim();
    if (!displayName) {
      toast.error('Customer name is required');
      return;
    }
    setSavingCustomer(true);
    try {
      // 1) Persist to customer DB.
      const payload = {
        ...customerForm,
        display_name: displayName,
        // Empty strings → null so Pydantic optional fields don't get rejected
        // for the EmailStr type when blank.
        email: customerForm.email?.trim() || null,
      };
      // Strip empty strings so the backend's "only-update-non-None" rule
      // doesn't wipe fields the SM didn't touch.
      Object.keys(payload).forEach(k => {
        if (payload[k] === '' || payload[k] === undefined) payload[k] = null;
      });
      const r1 = await fetch(`${BACKEND_URL}/api/customers/${invoice.customer_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r1.ok) {
        const err = await r1.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update customer');
        return;
      }
      const updatedCustomer = await r1.json();

      // 2) Refresh the invoice snapshot. Re-saving the invoice with the
      // same customer_id makes the backend re-pull customer_name/phone/
      // email from the customer doc (see routers/invoices.update_invoice).
      // Only call this for invoices that already exist (have an id).
      if (!isNewInvoice && invoiceId) {
        await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customer_id: invoice.customer_id }),
        });
      }

      // 3) Reflect changes in the on-screen invoice immediately.
      // iter138.9r — explicit nullish-coalescing to `null` (NOT to prev)
      // so an SM CLEARING a field (mobile/email/etc) actually clears the
      // UI too. The earlier `?? prev.customer_*` fallback re-introduced
      // the stale value when the new value was null (cleared) — fixed by
      // iter120 testing agent finding.
      const friendlyName = updatedCustomer.display_name
        || `${updatedCustomer.first_name || ''} ${updatedCustomer.last_name || ''}`.trim();
      setInvoice(prev => ({
        ...prev,
        customer_name: friendlyName,
        customer_phone: updatedCustomer.mobile ?? updatedCustomer.phone ?? null,
        customer_email: updatedCustomer.email ?? null,
      }));
      // Refresh the `customer` state, which feeds the Invoice Information
      // panel (line 2043: `customer?.email || invoice.customer_email`).
      // Without this, the panel + "Email invoice" CTA keep showing the
      // OLD email even after a successful save because `customer.email`
      // (stale, from initial page load) wins the `||` over the refreshed
      // invoice snapshot. Fixes user report: "FROM INVOICE PAGE I AM
      // TRYING EDIT EMAIL ID OG CUSTOMER ... BUT IT IS NOT UPDATING ...
      // AND NOT REFLECTING INVOICE PAGE".
      setCustomer(updatedCustomer);
      toast.success('Customer details saved');
      setShowEditCustomer(false);
    } catch (e) {
      console.error(e);
      toast.error('Failed to save customer details');
    } finally {
      setSavingCustomer(false);
    }
  };

  const selectVehicle = (vehicleId) => {
    const vehicle = customerVehicles.find(v => v.vehicle_id === vehicleId);
    if (vehicle) {
      setInvoice(prev => ({
        ...prev,
        vehicle_id: vehicleId,
        vehicle_rego: vehicle.rego || '',
        vin_number: vehicle.vin || prev.vin_number,
        transmission: vehicle.transmission || prev.transmission
      }));
    } else {
      setInvoice(prev => ({
        ...prev,
        vehicle_id: '',
        vehicle_rego: ''
      }));
    }
  };

  // Open the Edit Vehicle Details modal. Fetches the LATEST vehicle doc
  // (not the invoice snapshot) so SM is editing real data — important
  // because the invoice may have been opened with stale snapshot values.
  const openEditVehicle = async () => {
    if (!invoice.vehicle_id) {
      toast.error('Pick a vehicle first');
      return;
    }
    setShowEditVehicle(true);
    setLoadingVehicleEdit(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/vehicles/${invoice.vehicle_id}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const v = await res.json();
        setVehicleForm({
          rego: v.rego || '',
          make: v.make || '',
          model: v.model || '',
          year: v.year ?? '',
          variant: v.variant || '',
          vin: v.vin || '',
          color: v.color || '',
          odometer: v.odometer ?? '',
        });
      } else {
        toast.error('Failed to load vehicle details');
        setShowEditVehicle(false);
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load vehicle details');
      setShowEditVehicle(false);
    } finally {
      setLoadingVehicleEdit(false);
    }
  };

  // Save the edited vehicle. Mirrors the customer flow:
  //   1) PUT /api/vehicles/{id} (writes to the vehicle DB)
  //   2) Refresh the invoice snapshot fields so the on-screen + PDF match
  //   3) Refresh customerVehicles dropdown options inline so the new
  //      rego/make/model immediately replaces the old label.
  const saveVehicleDetails = async () => {
    if (!invoice.vehicle_id) return;
    if (!(vehicleForm.rego || '').trim()) {
      toast.error('Rego is required');
      return;
    }
    setSavingVehicle(true);
    try {
      // Coerce numeric fields. Empty strings → null so unset stays unset.
      const payload = {
        rego: (vehicleForm.rego || '').trim().toUpperCase() || null,
        make: (vehicleForm.make || '').trim() || null,
        model: (vehicleForm.model || '').trim() || null,
        year: vehicleForm.year === '' || vehicleForm.year == null ? null : Number(vehicleForm.year),
        variant: (vehicleForm.variant || '').trim() || null,
        vin: (vehicleForm.vin || '').trim().toUpperCase() || null,
        color: (vehicleForm.color || '').trim() || null,
        odometer: vehicleForm.odometer === '' || vehicleForm.odometer == null ? null : Number(vehicleForm.odometer),
      };
      const r = await fetch(`${BACKEND_URL}/api/vehicles/${invoice.vehicle_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update vehicle');
        return;
      }
      const updatedVehicle = await r.json();

      // Update the on-screen invoice snapshot. The PDF reads the
      // denormalised vehicle_rego/make/model/year/vin from the invoice
      // doc so we patch all of them so the next print is correct.
      setInvoice(prev => ({
        ...prev,
        vehicle_rego: updatedVehicle.rego || prev.vehicle_rego,
        vehicle_make: updatedVehicle.make || prev.vehicle_make,
        vehicle_model: updatedVehicle.model || prev.vehicle_model,
        vehicle_year: updatedVehicle.year || prev.vehicle_year,
        vin_number: updatedVehicle.vin || prev.vin_number,
        vehicle_color: updatedVehicle.color || prev.vehicle_color,
      }));

      // Refresh the dropdown options so the relabel sticks. Only call if
      // we have a customer (which we should — vehicles are scoped per
      // customer).
      if (invoice.customer_id) {
        fetchCustomerVehicles(invoice.customer_id);
      }

      // Persist the snapshot back to the invoice so anyone re-opening
      // the invoice (or downloading the PDF) sees the new values too.
      // Only fire if the invoice already exists server-side.
      if (!isNewInvoice && invoiceId) {
        await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            vehicle_rego: updatedVehicle.rego,
            vehicle_make: updatedVehicle.make,
            vehicle_model: updatedVehicle.model,
            vehicle_year: updatedVehicle.year,
            vin_number: updatedVehicle.vin,
          }),
        });
      }

      toast.success('Vehicle details saved');
      setShowEditVehicle(false);
    } catch (e) {
      console.error(e);
      toast.error('Failed to save vehicle details');
    } finally {
      setSavingVehicle(false);
    }
  };

  const updateField = (field, value) => {
    setInvoice(prev => ({ ...prev, [field]: value }));
  };

  // Line item management
  // NEW invoices restrict line items to structured categories only
  // (part_and_labour / part / labour) — Simple is legacy-only. Existing
  // invoices with Simple rows still work and remain editable.
  const isNewInvoiceFlow = !invoiceId || isNewInvoice;

  const addNewLine = (type = 'part') => {
    // Every new line is structured by default — Service Manager must enter
    // ORP/RRP/Margin/Qty/Hrs/Rate so daily ORP rollup + margin reporting
    // work. The legacy `simple` category is reserved for historical/imported
    // rows only — never created fresh in the UI anymore.
    const defaultCategory =
      type === 'labour' ? 'labour'
      : type === 'tyre' ? 'tyre'
      : 'part_and_labour';
    const newLine = {
      line_id: generateLineId(),
      item_type: type,
      category: defaultCategory,
      description: type === 'tyre' ? 'Tyre' : '',
      part_number: '',
      quantity: type === 'tyre' ? 4 : 1,  // tyres normally come 4-at-a-time
      unit: type === 'labour' ? 'hours' : 'each',
      unit_price: 0,
      line_total: 0,
      order: invoice.line_items.length,
      // Seed structured defaults so the breakdown row renders inputs.
      parts_price: 0,
      parts_cost_price: null,
      parts_supplier: null,
      // iter225 — tyre margin is MANUAL (blank until SM types it); other
      // categories seed from the per-site default margin.
      parts_margin_pct: type === 'tyre' ? '' : (workshopDefaultMargin ?? 40),
      labour_rate: workshopLabourRate || 125.5,
      labour_hours: type === 'labour' ? 1 : 0,
      // Tyre-specific flat fitting/balancing/valve/disposal fee per tyre.
      tyre_flat_rate: type === 'tyre' ? 0 : null,
    };
    setInvoice(prev => ({
      ...prev,
      line_items: [...prev.line_items, newLine]
    }));
    // Auto-expand the just-added line so SM sees the inputs immediately.
    setExpandedLineIds(prev => new Set([...prev, newLine.line_id]));
  };

  // Compute a structured line's total. Uses the shared helper in
  // /app/frontend/src/lib/pricing.js so the formula is identical across
  // ServiceNotesModal, CustomItemSearch, QuoteBuilder and this page.
  //   line_total = qty × unit_price  (unit_price = parts + labour, per unit)
  const recomputeLine = (item, workshopRate = 125.5) => {
    const cat = item.category || 'simple';
    const qty = parseFloat(item.quantity) || 0;
    if (cat === 'simple') {
      const price = parseFloat(item.unit_price) || 0;
      return {
        ...item,
        parts_charge: null,
        labour_charge: null,
        unit_price: price,
        line_total: Math.round(qty * price * 100) / 100,
      };
    }
    const wantsParts = cat === 'part' || cat === 'part_and_labour' || cat === 'tyre';
    const wantsLabour = cat === 'labour' || cat === 'part_and_labour';
    // Default margin: tyre is MANUAL ('' = 0%); legacy tyre rows saved under
    // the locked-30% era (margin null) keep 30 so totals don't shift when an
    // unrelated field is edited. Others use the per-site default.
    const legacyTyre = cat === 'tyre' && item.parts_margin_pct == null;
    const defaultMargin = cat === 'tyre' ? (legacyTyre ? 30 : 0) : (workshopDefaultMargin ?? 40);
    const margin = wantsParts
      ? (item.parts_margin_pct === '' || item.parts_margin_pct == null
          ? defaultMargin
          : parseFloat(item.parts_margin_pct))
      : 0;
    const labourRate = wantsLabour ? (parseFloat(item.labour_rate) || workshopRate) : 0;
    const p = computeRowPricing(
      { ...item, parts_margin_pct: margin, labour_rate: labourRate },
      workshopRate,
    );
    return {
      ...item,
      parts_price: wantsParts ? (parseFloat(item.parts_price) || 0) : 0,
      parts_cost_price: item.parts_cost_price === '' || item.parts_cost_price == null
        ? null
        : Number(item.parts_cost_price),
      parts_supplier: (item.parts_supplier ?? '').toString().trim() || null,
      parts_margin_pct: margin,
      // Tyre rows have no labour fields — null them so reporting is clean.
      labour_rate: cat === 'tyre' ? null : labourRate,
      labour_hours: cat === 'tyre' ? null : (wantsLabour ? (parseFloat(item.labour_hours) || 0) : 0),
      tyre_flat_rate: cat === 'tyre'
        ? (parseFloat(item.tyre_flat_rate) || 0)
        : null,
      parts_charge: p.parts_charge,
      labour_charge: p.labour_charge,
      unit_price: p.unit_price_avg,
      line_total: p.line_total,
    };
  };

  const updateLineItem = (lineId, field, value) => {
    // Sticky expand — once the SM touches ANY structured field on a row,
    // keep it expanded until they explicitly collapse via the chevron.
    // Without this, lineIncomplete flips false the moment ORP+Supplier+
    // Margin land — and the row auto-collapses MID-EDIT, dropping the
    // supplier dropdown / hiding the labour fields. User report:
    // "after typing one character in supplier, it closes."
    setExpandedLineIds(prev => {
      if (prev.has(lineId)) return prev;
      const next = new Set(prev);
      next.add(lineId);
      return next;
    });
    setInvoice(prev => ({
      ...prev,
      line_items: prev.line_items.map(item => {
        if (item.line_id !== lineId) return item;
        // If switching category, reset structured fields that don't apply
        let merged = { ...item, [field]: value };
        if (field === 'category') {
          // Initialise structured defaults on switch
          if (value === 'part_and_labour' || value === 'part') {
            merged.parts_price = merged.parts_price ?? 0;
            merged.parts_margin_pct = merged.parts_margin_pct ?? (workshopDefaultMargin ?? 40);
          }
          if (value === 'part_and_labour' || value === 'labour') {
            merged.labour_rate = merged.labour_rate ?? (workshopLabourRate || 125.5);
            merged.labour_hours = merged.labour_hours ?? 0;
          }
          if (value === 'tyre') {
            // iter225 — tyre margin is MANUAL: keep whatever the row already
            // has, otherwise leave BLANK for the SM to type. No RRP concept
            // on tyre rows — parts_price zeroed.
            merged.parts_margin_pct = merged.parts_margin_pct ?? '';
            merged.parts_price = 0;
            merged.parts_cost_price = merged.parts_cost_price ?? 0;
            merged.tyre_flat_rate = merged.tyre_flat_rate ?? 0;
            // Labour fields don't apply to tyre rows — null them so the
            // line_total math is correct.
            merged.labour_rate = null;
            merged.labour_hours = null;
          }
        }
        // ORP → RRP auto-fill (NOT on tyre — no RRP concept there).
        if (field === 'parts_cost_price' && merged.category !== 'tyre') {
          const rrp = orpToRrpHelper(value, workshopOrpMargin);
          if (rrp !== '') merged.parts_price = rrp;
        }
        return recomputeLine(merged, workshopLabourRate || 125.5);
      })
    }));
  };

  const deleteLineItem = (lineId) => {
    setInvoice(prev => ({
      ...prev,
      line_items: prev.line_items.filter(item => item.line_id !== lineId)
    }));
  };

  // iter138.9e — Drag-and-drop reorder via @dnd-kit. The SM grabs the
  // 6-dot GripVertical handle on a line and drags it to its new
  // position. Works equally on desktop mouse (PointerSensor) and iPad
  // touch (TouchSensor with 150 ms long-press to avoid hijacking
  // scroll). On drop we move the active row to the over-row's index
  // and re-number `order` so the new sequence persists end-to-end.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    // 150 ms long-press on iPad before drag activates so the SM can
    // still scroll the invoice page with a normal swipe.
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const reorderLineItems = (activeId, overId) => {
    if (!activeId || !overId || activeId === overId) return;
    setInvoice(prev => {
      const items = [...prev.line_items];
      const from = items.findIndex(it => it.line_id === activeId);
      const to = items.findIndex(it => it.line_id === overId);
      if (from < 0 || to < 0 || from === to) return prev;
      const [moved] = items.splice(from, 1);
      items.splice(to, 0, moved);
      // Re-number `order` so the new sequence persists when saved.
      return { ...prev, line_items: items.map((it, i) => ({ ...it, order: i })) };
    });
  };

  // === Parts Catalog Functions ===
  const fetchPartsCatalog = async (search = '') => {
    setLoadingParts(true);
    try {
      const params = new URLSearchParams({ ...getSiteParams() });
      if (search) params.append('search', search);
      
      const response = await fetch(`${BACKEND_URL}/api/parts-catalog?${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPartsCatalog(data.parts || []);
      }
    } catch (error) {
      console.error('Failed to fetch parts:', error);
    } finally {
      setLoadingParts(false);
    }
  };

  const addPartFromCatalog = (part) => {
    const newLine = {
      line_id: generateLineId(),
      item_type: 'part',
      description: part.description,
      part_number: part.part_number,
      quantity: 1,
      unit: part.unit || 'each',
      unit_price: part.unit_price,
      line_total: part.unit_price,
      order: invoice.line_items.length
    };
    setInvoice(prev => ({
      ...prev,
      line_items: [...prev.line_items, newLine]
    }));
    toast.success(`Added: ${part.description}`);
  };

  // Create new part
  const handleCreatePart = async () => {
    if (!newPart.part_number || !newPart.description || !newPart.unit_price) {
      toast.error('Please fill part number, description, and price');
      return;
    }
    
    setSavingPart(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/parts-catalog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newPart,
          unit_price: parseFloat(newPart.unit_price) || 0
        })
      });
      
      if (response.ok) {
        const createdPart = await response.json();
        toast.success('Part created and added to invoice');
        // Add to invoice line items
        addPartFromCatalog(createdPart);
        // Refresh parts catalog
        fetchPartsCatalog(partsSearchQuery);
        // Reset form and close dialog
        setNewPart({ part_number: '', description: '', unit_price: '', unit: 'each', category: 'other' });
        setShowCreatePartDialog(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create part');
      }
    } catch (error) {
      console.error('Failed to create part:', error);
      toast.error('Failed to create part');
    } finally {
      setSavingPart(false);
    }
  };

  // === Templates Functions ===
  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const params = new URLSearchParams({ ...getSiteParams() });
      const response = await fetch(`${BACKEND_URL}/api/templates?${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Convert a saved-template line item into a structured invoice line.
  // Carries every structured field forward so SM doesn't lose ORP / RRP /
  // margin / labour breakdown when applying a saved template. Falls back
  // to part_and_labour when the template was saved before structured
  // fields were introduced (legacy "simple" templates with unit_price only).
  const _templateLineToInvoiceLine = (item, idx, baseOrder) => {
    const hasStructured =
      item.parts_price != null
      || item.parts_cost_price != null
      || item.parts_margin_pct != null
      || item.labour_hours != null
      || item.labour_rate != null;
    const inferredCategory = item.category
      || (hasStructured ? 'part_and_labour'
          : (item.item_type === 'labour' ? 'labour' : 'part_and_labour'));
    // Build the candidate line. For legacy simple-only templates with
    // just unit_price, seed parts_price from unit_price so the SM gets
    // SOMETHING to edit (vs starting at $0). Margin defaults to 40%.
    const candidate = {
      line_id: generateLineId(),
      item_type: item.item_type || 'service',
      category: inferredCategory,
      description: item.description || '',
      part_number: item.part_number || '',
      quantity: Number(item.quantity) || 1,
      unit: item.unit || (inferredCategory === 'labour' ? 'hours' : 'each'),
      parts_supplier: item.parts_supplier || null,
      parts_cost_price: item.parts_cost_price ?? null,
      parts_price: item.parts_price != null
        ? Number(item.parts_price)
        : (inferredCategory === 'labour' ? 0 : Number(item.unit_price) || 0),
      parts_margin_pct: item.parts_margin_pct != null ? Number(item.parts_margin_pct) : 40,
      labour_rate: item.labour_rate != null ? Number(item.labour_rate) : (workshopLabourRate || 125.5),
      labour_hours: item.labour_hours != null
        ? Number(item.labour_hours)
        : (inferredCategory === 'labour' ? (Number(item.quantity) || 1) : 0),
      unit_price: Number(item.unit_price) || 0,
      line_total: 0,
      order: baseOrder + idx,
    };
    return recomputeLine(candidate, workshopLabourRate || 125.5);
  };

  const applyTemplate = (template) => {
    if (template.category === 'service_note') {
      // Apply service note to invoice notes
      setInvoice(prev => ({
        ...prev,
        notes: template.content
      }));
      toast.success(`Applied note: ${template.name}`);
    } else if (template.category === 'invoice_template' && template.line_items?.length > 0) {
      // Apply full invoice template with line items — preserve structured
      // pricing data so the SM lands with ORP/RRP/margin/labour pre-filled
      // and can edit per line.
      const baseOrder = invoice.line_items.length;
      const newLines = template.line_items.map((item, idx) =>
        _templateLineToInvoiceLine(item, idx, baseOrder),
      );
      setInvoice(prev => ({
        ...prev,
        notes: template.content || prev.notes,
        line_items: [...prev.line_items, ...newLines]
      }));
      toast.success(`Applied template: ${template.name}`);
    }
    setShowTemplatesDialog(false);
  };

  // Fetch invoice templates (custom item templates)
  const fetchInvoiceTemplates = async () => {
    setLoadingInvoiceTemplates(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/templates?category=invoice_template`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setInvoiceTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Failed to fetch invoice templates:', error);
    } finally {
      setLoadingInvoiceTemplates(false);
    }
  };

  // Apply custom item template to invoice — preserves structured fields
  // (supplier / ORP / RRP / margin / labour) so SM can edit per line.
  const applyCustomItemTemplate = (template) => {
    if (!template.line_items?.length) {
      toast.error('This template has no items');
      return;
    }
    const baseOrder = invoice.line_items.length;
    const newLines = template.line_items.map((item, idx) =>
      _templateLineToInvoiceLine(item, idx, baseOrder),
    );

    setInvoice(prev => ({
      ...prev,
      line_items: [...prev.line_items, ...newLines]
    }));

    toast.success(`Added ${newLines.length} items from "${template.name}"`);
    setShowCustomItemsDialog(false);
    setCustomItemSearchQuery('');
  };

  // Filter invoice templates by search
  const filteredInvoiceTemplates = invoiceTemplates.filter(t =>
    t.name.toLowerCase().includes(customItemSearchQuery.toLowerCase()) ||
    (t.content || '').toLowerCase().includes(customItemSearchQuery.toLowerCase())
  );

  // Create new template from current invoice line items
  const handleCreateTemplate = async () => {
    if (!newTemplate.name) {
      toast.error('Please enter a template name');
      return;
    }
    
    setSavingTemplate(true);
    try {
      // If creating invoice template, snapshot the current line items with
      // FULL structured shape so re-applying the template restores ORP/RRP/
      // margin/qty/hours/rate exactly as the SM had them when saving.
      const templateData = {
        name: newTemplate.name,
        category: newTemplate.category,
        content: newTemplate.content || invoice.notes || '',
        line_items: newTemplate.category === 'invoice_template'
          ? invoice.line_items.map(item => ({
              item_type: item.item_type || 'part',
              category: item.category || 'part_and_labour',
              description: item.description,
              part_number: item.part_number,
              quantity: item.quantity,
              unit: item.unit,
              unit_price: item.unit_price,
              // Structured pricing fields — these are the values the SM
              // tunes per line; preserving them is the whole point of a
              // template. Null entries are kept so the apply path knows to
              // initialise sane defaults rather than silently using 0.
              parts_supplier: item.parts_supplier ?? null,
              parts_cost_price: item.parts_cost_price ?? null,
              parts_price: item.parts_price ?? null,
              parts_margin_pct: item.parts_margin_pct ?? null,
              labour_hours: item.labour_hours ?? null,
              labour_rate: item.labour_rate ?? null,
            }))
          : []
      };
      
      const response = await fetch(`${BACKEND_URL}/api/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(templateData)
      });
      
      if (response.ok) {
        toast.success('Template created successfully');
        fetchTemplates();
        setNewTemplate({ name: '', category: 'invoice_template', content: '', line_items: [] });
        setShowCreateTemplateDialog(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create template');
      }
    } catch (error) {
      console.error('Failed to create template:', error);
      toast.error('Failed to create template');
    } finally {
      setSavingTemplate(false);
    }
  };

  // === PDF & Email Functions ===
  const handlePrintPdf = async () => {
    if (isNewInvoice) {
      toast.error('Please save the invoice first');
      return;
    }

    setGeneratingPdf(true);

    // Performance-tuned print flow:
    //   1. window.open() synchronously so popup blockers stay happy.
    //   2. Fetch the PDF concurrently with the window opening — no
    //      loader HTML write (saved a DOM round-trip + paint cycle).
    //   3. Browser renders the binary PDF in its built-in viewer — no
    //      browser-injected date/URL header (only happens for HTML
    //      printed via window.print()).
    const printWindow = window.open('about:blank', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups for this site to print invoices');
      setGeneratingPdf(false);
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}/pdf${getSiteParams()}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to generate invoice');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      printWindow.location.replace(url);
      // Free the blob URL once the new viewer has loaded.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Failed to generate invoice:', error);
      try {
        printWindow.document.body.innerHTML =
          '<div style="font-family:system-ui;padding:40px;text-align:center;color:#444;"><h2>Could not load invoice</h2><p>Please close this window and try again.</p></div>';
      } catch (_e) { /* window already closed */ }
      toast.error('Failed to generate invoice');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/template-library?template_type=email`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setEmailTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Failed to fetch email templates:', error);
    }
  };

  const handleOpenEmailModal = () => {
    if (isNewInvoice) {
      toast.error('Please save the invoice first');
      return;
    }
    // iter138.9n — Guard against missing customer email up-front so the
    // SM doesn't open the modal, pick a template, click Send, and then
    // see a confusing 400 toast from the backend.
    const targetEmail = customer?.email || invoice.customer_email;
    if (!targetEmail) {
      toast.error('No customer email on file. Edit the customer and add an email address, then try again.');
      return;
    }
    fetchEmailTemplates();
    setSelectedEmailTemplate('');
    setShowEmailModal(true);
  };

  const handleEmailInvoice = async () => {
    if (isNewInvoice) {
      toast.error('Please save the invoice first');
      return;
    }
    setSendingEmail(true);
    try {
      const body = selectedEmailTemplate
        ? { template_id: selectedEmailTemplate }
        : {};
      const response = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok) {
        toast.success(
          (customer?.email || invoice.customer_email)
            ? `Invoice (with PDF) emailed to ${customer?.email || invoice.customer_email}`
            : 'Invoice emailed successfully'
        );
        setShowEmailModal(false);
        setSelectedEmailTemplate('');
      } else {
        // iter138.9n — Surface the BE detail verbatim. BE returns
        // human-readable messages like "No customer email on file…" or
        // "Failed to send email: <SendGrid error>". Show with longer
        // duration so SM can read it.
        const errorMsg = result.detail || result.message || `Failed to send email (HTTP ${response.status})`;
        toast.error(errorMsg, { duration: 8000 });
      }
    } catch (error) {
      console.error('Failed to email invoice:', error);
      toast.error('Failed to email invoice');
    } finally {
      setSendingEmail(false);
    }
  };

  const fetchSmsTemplates = async () => {
    try {
      // iter121: unified template-library — same source as Super Admin →
      // Templates → SMS tab and the JC SMS modal. Returns docs shaped
      // {template_id, name, content:{body}}. The legacy
      // `/api/marketing/templates?channel=sms` was a parallel collection
      // that SMs couldn't manage centrally.
      const response = await fetch(`${BACKEND_URL}/api/template-library?template_type=sms`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSmsTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Failed to fetch SMS templates:', error);
    }
  };

  const handleOpenSmsModal = () => {
    if (isNewInvoice) {
      toast.error('Please save the invoice first');
      return;
    }
    // iter123: default the recipient checkboxes based on what's
    // available. Mobile is preferred; landline only auto-ticks when
    // there's no mobile.
    const mobile = customer?.mobile || invoice.customer_phone || '';
    const landline = customer?.phone || invoice.customer_landline || '';
    setSmsTargets({
      mobile: !!mobile,
      landline: !!landline && !mobile,
    });
    fetchSmsTemplates();
    setShowSmsModal(true);
  };

  // Send the invoice PDF via WhatsApp using the seeded "Share
  // Recommended Repair Quote" unified template (kind=quote_with_pdf).
  // Backend handles: PDF render → Meta media upload → document send.
  // Fails gracefully with the underlying Meta error if WhatsApp creds
  // aren't configured yet.
  const handleSendWhatsApp = async () => {
    if (isNewInvoice) {
      toast.error('Please save the invoice first');
      return;
    }
    setSendingWhatsApp(true);
    try {
      // 1. Find the quote_with_pdf template to use its caption + body params.
      let templateId = null;
      try {
        const r = await fetch(`${BACKEND_URL}/api/messaging-templates`, { credentials: 'include' });
        if (r.ok) {
          const list = await r.json();
          const quote = (list || []).find(t => t.kind === 'quote_with_pdf' && (t.channels || []).includes('whatsapp'));
          templateId = quote?.template_id || null;
        }
      } catch (_e) { /* fallback to no-template — backend uses its own default caption */ }
      // 2. Fire the send.
      const r2 = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}/send-whatsapp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(templateId ? { template_id: templateId } : {}),
      });
      if (r2.ok) {
        const d = await r2.json();
        toast.success(`WhatsApp sent to ${d.to} with PDF attached`);
      } else {
        const e = await r2.json().catch(() => ({}));
        toast.error(e.detail || 'WhatsApp send failed');
      }
    } catch (_e) {
      toast.error('Network error sending WhatsApp');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handleSendSms = async () => {
    const template = smsTemplates.find(t => t.template_id === selectedSmsTemplate);
    // iter121: new template-library shape stores body under `content.body`.
    // Fall back to legacy `content` string for older docs.
    const templateBody = template
      ? (typeof template.content === 'string'
          ? template.content
          : (template.content?.body || ''))
      : '';
    const message = template
      ? templateBody
        .replace(/{customer_name}/g, invoice.customer_name || 'Customer')
        .replace(/{vehicle_rego}/g, invoice.vehicle_rego || '')
        .replace(/{invoice_total}/g, `$${totals.total.toFixed(2)}`)
        .replace(/{invoice_id}/g, invoice.doc_no || '')
      : customSmsMessage;

    if (!quoteSmsMode && !message.trim()) {
      toast.error('Please select a template or write a custom message');
      return;
    }

    setSendingSms(true);
    try {
      // iter123: build target list from the multi-recipient picker.
      const targets = [];
      const mobile = customer?.mobile || invoice.customer_phone || '';
      const landline = customer?.phone || invoice.customer_landline || '';
      if (smsTargets.mobile && mobile) targets.push(mobile);
      if (smsTargets.landline && landline && landline !== mobile) targets.push(landline);
      if (!quoteSmsMode && targets.length === 0) {
        toast.error('Tick at least one number (mobile or landline) to send to');
        setSendingSms(false);
        return;
      }

      // Quote-SMS mode: backend mints a token + builds its own templated
      // message + sends via CellCast. We just hit the dedicated endpoint
      // (no message body needed).
      const endpoint = quoteSmsMode
        ? `${BACKEND_URL}/api/invoices/${invoiceId}/send-quote-sms`
        : `${BACKEND_URL}/api/invoices/${invoiceId}/send-sms`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(quoteSmsMode ? {} : { message, targets })
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        if (quoteSmsMode) {
          toast.success(`Quote SMS sent — link: ${data.link}`);
          setQuoteSmsResult(data);
          // Keep modal open so SM can copy/share the link too
        } else {
          toast.success('SMS sent successfully');
          setShowSmsModal(false);
          setSelectedSmsTemplate('');
          setCustomSmsMessage('');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send SMS');
      }
    } catch (error) {
      console.error('Failed to send SMS:', error);
      toast.error('Failed to send SMS');
    } finally {
      setSendingSms(false);
    }
  };

  // Validate all structured line items have the mandatory fields filled in.
  // RRP, Labour rate and Labour hours may all be ZERO — SM is allowed to
  // give a special price (goodwill / in-house courtesy / no-charge line)
  // by leaving any of those at 0. Margin is still enforced ONLY when
  // RRP > 0 (a paid parts row must still hit the 40 % floor). Supplier
  // is required only when RRP > 0 since a $0 row has no supplier to
  // track. Description always required.
  // ORP (parts_cost_price) stays optional — supplier may bill monthly.
  const validateStructuredLines = (lines) => {
    const errors = [];
    lines.forEach((li, idx) => {
      const cat = li.category || 'simple';
      if (cat === 'simple') return; // legacy rows skip
      const lineNo = idx + 1;
      const desc = (li.description || '').trim();
      if (!desc) errors.push(`Line ${lineNo}: Description is required`);
      const needsParts = cat === 'part' || cat === 'part_and_labour';
      if (needsParts) {
        const rrp = Number(li.parts_price) || 0;
        if (rrp > 0) {
          if (!li.parts_supplier || !li.parts_supplier.toString().trim()) {
            errors.push(`Line ${lineNo} (${desc || 'no description'}): Supplier is required when RRP > $0`);
          }
          // Margin floor REMOVED (SM request, Aug 2026) — parts margin is
          // fully flexible now; no minimum is enforced. Supplier is still
          // required when RRP > $0 (unchanged).
        }
      }
      // Labour: rate and hours are both free to be 0 (goodwill / courtesy).
    });
    return errors;
  };

  const handleSave = async ({ skipLineValidation = false } = {}) => {
    if (!invoice.customer_id) {
      toast.error('Please select a customer');
      return false;
    }

    // Block save until every structured line has supplier / RRP / margin /
    // labour rate / labour hours. ORP stays optional (some suppliers bill
    // monthly so the SM enters cost after the fact).
    //
    // skipLineValidation is set by INTERNAL preflight saves (e.g. when the
    // Service Notes modal pushes approved notes → invoice lines). Those
    // flows are persisting modal state (notes_items, approvals, edits) —
    // they shouldn't be blocked by unrelated partial line_items rows the
    // SM hasn't filled out yet. The full validation still gates the
    // explicit Save / Send / Mark-Paid actions.
    if (!skipLineValidation) {
      const lineErrors = validateStructuredLines(invoice.line_items || []);
      if (lineErrors.length > 0) {
        const head = lineErrors.slice(0, 3).join('\n');
        const tail = lineErrors.length > 3 ? `\n…and ${lineErrors.length - 3} more` : '';
        toast.error(
          `Fix ${lineErrors.length} issue${lineErrors.length === 1 ? '' : 's'} before saving:\n${head}${tail}`,
          { duration: 10000 },
        );
        return false;
      }
    }
    
    setSaving(true);
    try {
      const payload = {
        ...invoice,
        invoice_date: invoice.invoice_date ? new Date(invoice.invoice_date).toISOString() : null,
        due_date: invoice.due_date ? new Date(invoice.due_date).toISOString() : null,
        service_date: invoice.service_date ? new Date(invoice.service_date).toISOString() : null,
        next_service_date: invoice.next_service_date ? new Date(invoice.next_service_date).toISOString() : null,
        rego_expiry: invoice.rego_expiry ? new Date(invoice.rego_expiry).toISOString() : null,
        odometer_reading: invoice.odometer_reading ? parseInt(invoice.odometer_reading) : null,
        discount_value: parseFloat(invoice.discount_value) || 0,
        amount_paid: parseFloat(invoice.amount_paid) || 0
      };
      
      let response;
      if (isNewInvoice) {
        response = await fetch(`${BACKEND_URL}/api/invoices`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      }
      
      if (response.ok) {
        const savedInvoice = await response.json();
        toast.success(`Invoice ${savedInvoice.doc_no} saved`);
        
        if (isNewInvoice) {
          navigate(`/invoices/${savedInvoice.invoice_id}`);
        } else {
          // Update local state with server response
          setInvoice(prev => ({
            ...prev,
            ...savedInvoice,
            invoice_date: savedInvoice.invoice_date ? savedInvoice.invoice_date.split('T')[0] : '',
            due_date: savedInvoice.due_date ? savedInvoice.due_date.split('T')[0] : '',
            service_date: savedInvoice.service_date ? savedInvoice.service_date.split('T')[0] : '',
            next_service_date: savedInvoice.next_service_date ? savedInvoice.next_service_date.split('T')[0] : '',
            rego_expiry: savedInvoice.rego_expiry ? savedInvoice.rego_expiry.split('T')[0] : ''
          }));
        }
        return true;
      } else {
        const error = await response.json().catch(() => ({}));
        const msg = typeof error.detail === 'string' ? error.detail : (Array.isArray(error.detail) ? error.detail.map(e => e.msg).join(' · ') : 'Failed to save invoice');
        toast.error(msg);
        return false;
      }
    } catch (error) {
      console.error('Failed to save invoice:', error);
      toast.error('Failed to save invoice — check console for details');
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout user={user}>
      {/* iter148 — Per-page zoom controls (floating, top-right). Live outside
          the transformed wrapper so they stay a consistent size. */}
      <div className="sticky top-2 z-40 flex justify-end pr-4 pointer-events-none">
        <div className="pointer-events-auto" data-testid="invoice-page-zoom-wrapper">
          <PageZoomControls
            scale={pageScale}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
            onReset={resetZoom}
            minScale={MIN_SCALE}
            maxScale={MAX_SCALE}
            label="Invoice zoom"
            testIdPrefix="invoice-zoom"
          />
        </div>
      </div>
      <div
        style={{
          transform: `scale(${pageScale})`,
          transformOrigin: 'top left',
          width: `${100 / pageScale}%`,
          transition: 'transform 120ms ease-out',
        }}
        data-testid="invoice-zoom-wrapper"
      >
      <div className="p-4 md:p-6 lg:p-8" data-testid="invoice-edit-container">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/invoices')} className="p-2">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-['Chivo'] font-bold text-2xl sm:text-3xl text-slate-900">
                {isNewInvoice ? 'New Invoice' : `Invoice ${invoice.doc_no}`}
              </h1>
              {!isNewInvoice && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Select
                    value={invoice.status || 'draft'}
                    onValueChange={(v) => updateField('status', v)}
                  >
                    <SelectTrigger
                      className={`h-7 px-2 py-0 text-xs font-semibold border-0 w-auto inline-flex gap-1 ${STATUS_CONFIG[invoice.status]?.color || 'bg-slate-100 text-slate-700'}`}
                      data-testid="invoice-status-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                        <SelectItem key={k} value={k} data-testid={`invoice-status-option-${k}`}>
                          {v.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* iter138.9ae — clickable On-Account toggle.
                      Auto-ON when customer has an active credit account
                      (server side does this on create). SM can flip it
                      OFF if customer paid cash/card on the spot.
                      
                      Click immediately PUTs /api/invoices/{id} so the
                      change is persisted even if the SM navigates away
                      without hitting Save Invoice. We optimistically
                      update local state first, then roll back on failure. */}
                  {accountInfo && (
                    <button
                      type="button"
                      disabled={isNewInvoice || togglingAccount}
                      onClick={async () => {
                        if (isNewInvoice) {
                          toast.info('Save the invoice once first, then toggle account billing.');
                          return;
                        }
                        const prev = {
                          is_on_account: invoice.is_on_account,
                          account_id: invoice.account_id,
                          payment_method: invoice.payment_method,
                        };
                        const next = !invoice.is_on_account;
                        const patch = {
                          is_on_account: next,
                          account_id: next ? accountInfo.account_id : null,
                          payment_method: next ? 'account' : null,
                        };
                        // Optimistic local update.
                        updateField('is_on_account', patch.is_on_account);
                        updateField('account_id', patch.account_id);
                        updateField('payment_method', patch.payment_method);
                        setTogglingAccount(true);
                        try {
                          const r = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}`, {
                            method: 'PUT',
                            credentials: 'include',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(patch),
                          });
                          if (!r.ok) {
                            const e = await r.json().catch(() => ({}));
                            throw new Error(e.detail || 'Failed to update invoice');
                          }
                          toast.success(next
                            ? `Linked to ${accountInfo.account_name} — will appear on month-end statement`
                            : 'Removed from account — invoice no longer on credit account');
                        } catch (e) {
                          // Roll back the optimistic update.
                          updateField('is_on_account', prev.is_on_account);
                          updateField('account_id', prev.account_id);
                          updateField('payment_method', prev.payment_method);
                          toast.error(e.message || 'Could not toggle account billing');
                        } finally {
                          setTogglingAccount(false);
                        }
                      }}
                      title={invoice.is_on_account
                        ? `Click to UNBILL from ${accountInfo.account_name} (e.g. customer paid on the spot)`
                        : `Click to BILL TO ${accountInfo.account_name} (Net ${accountInfo.credit_terms_days || 30})`}
                      className={`h-7 px-2 inline-flex items-center gap-1 rounded text-xs font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                        invoice.is_on_account
                          ? (accountInfo.is_overdue
                              ? 'bg-rose-100 text-rose-800 border border-rose-300 hover:bg-rose-200'
                              : 'bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-200')
                          : 'bg-slate-100 text-slate-700 border border-dashed border-slate-300 hover:border-slate-400'
                      }`}
                      data-testid="account-pill-top"
                    >
                      {togglingAccount ? '… saving' : (invoice.is_on_account
                        ? (accountInfo.is_overdue ? '⚠ ON ACCOUNT — OVERDUE' : '★ ON ACCOUNT')
                        : '＋ Bill to account')}
                      <span className="font-normal opacity-80 hidden md:inline">· {accountInfo.account_name}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {!isNewInvoice && (
            <div className="flex items-center gap-2 ml-auto sm:ml-0">
              <Button 
                variant="outline" 
                size="sm" 
                disabled={generatingPdf}
                onClick={handlePrintPdf}
                data-testid="print-invoice-btn"
              >
                <Printer className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{generatingPdf ? 'Generating...' : 'Print'}</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={sendingEmail || (!customer?.email && !invoice.customer_email)}
                onClick={guardComms(handleOpenEmailModal)}
                data-testid="email-invoice-btn"
                title={
                  !customer?.email && !invoice.customer_email
                    ? 'No customer email on file. Edit the customer and add an email address.'
                    : `Email invoice (PDF attached) to ${customer?.email || invoice.customer_email}`
                }
              >
                <Mail className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{sendingEmail ? 'Sending...' : 'Email'}</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={sendingSms}
                onClick={guardComms(handleOpenSmsModal)}
                data-testid="sms-invoice-btn"
              >
                <MessageSquare className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">SMS</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={sendingWhatsApp}
                onClick={handleSendWhatsApp}
                data-testid="whatsapp-invoice-btn"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                title="Send quote PDF via WhatsApp"
              >
                <Send className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{sendingWhatsApp ? 'Sending…' : 'WhatsApp'}</span>
              </Button>
            </div>
          )}
        </div>

        {/* Main Content */}
        <Card className="p-6">
          {/* Invoice Information — 4-column layout (iter138.9h)
              Col 1 Customer · Col 2 Vehicle · Col 3 Invoice Summary · Col 4 Work Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4 mb-6 pb-6 border-b" data-testid="invoice-info-grid">

            {/* ───── COL 1 — Customer Details ───── */}
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">Customer Details</h3>
                {invoice.customer_id && (
                  <button
                    type="button"
                    onClick={openEditCustomer}
                    className="text-[11px] font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    data-testid="edit-customer-details-btn"
                  >
                    Edit
                  </button>
                )}
              </div>
              {memberInfo && (
                <div
                  className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50"
                  data-testid="invoice-member-badge"
                  title={`Membership${memberInfo.company_name ? ` — ${memberInfo.company_name}` : ''}: accumulated $${Number(memberInfo.accumulated || 0).toFixed(2)}, used $${Number(memberInfo.used || 0).toFixed(2)}`}
                >
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wider text-amber-800">
                    <Crown className="h-3.5 w-3.5 text-amber-500" />
                    Gold Member{memberInfo.status === 'suspended' ? ' (suspended)' : ''}
                  </span>
                  <span className="text-xs font-semibold text-slate-800" data-testid="invoice-member-balance">
                    Credit balance:{' '}
                    <span className={Number(memberInfo.balance || 0) < 0 ? 'text-rose-600' : 'text-emerald-700'}>
                      ${Number(memberInfo.balance || 0).toFixed(2)}
                    </span>
                  </span>
                </div>
              )}
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-slate-500">Name</Label>
                <div className="flex gap-2">
                  <Input
                    value={invoice.customer_name || ''}
                    readOnly
                    placeholder="Select customer..."
                    className="bg-slate-50 cursor-pointer h-9"
                    onClick={() => setShowCustomerSearch(true)}
                    data-testid="invoice-customer-name"
                  />
                  <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setShowCustomerSearch(true)}>
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                  <Phone className="h-3 w-3" /> Mobile
                </Label>
                <Input
                  value={customer?.mobile_phone || invoice.customer_phone || ''}
                  readOnly
                  className="bg-slate-50 h-9 font-mono text-sm"
                  placeholder="—"
                  data-testid="invoice-customer-mobile"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                  <Phone className="h-3 w-3" /> Phone (Landline)
                </Label>
                <Input
                  value={customer?.phone || ''}
                  readOnly
                  className="bg-slate-50 h-9 font-mono text-sm"
                  placeholder="—"
                  data-testid="invoice-customer-landline"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                  <Mail className="h-3 w-3" /> Email
                </Label>
                <Input
                  value={customer?.email ?? invoice.customer_email ?? ''}
                  readOnly
                  className="bg-slate-50 h-9 text-sm"
                  placeholder="—"
                  data-testid="invoice-customer-email"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-slate-500">Address</Label>
                <div
                  className="min-h-[64px] rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 whitespace-pre-line"
                  data-testid="invoice-customer-address"
                >
                  {(() => {
                    const parts = [
                      customer?.address_line1 || invoice.customer_address_line1,
                      customer?.address_line2 || invoice.customer_address_line2,
                      [customer?.suburb || invoice.customer_suburb,
                       customer?.state || invoice.customer_state,
                       customer?.postcode || invoice.customer_postcode].filter(Boolean).join(' '),
                    ].filter(p => (p || '').trim());
                    const legacy = customer?.address || invoice.customer_address;
                    if (parts.length) return parts.join('\n');
                    return legacy || '—';
                  })()}
                </div>
              </div>
              {/* OVERDUE banner (kept from previous layout — blocks vehicle release) */}
              {accountInfo && accountInfo.is_overdue && (
                <div className="p-2 rounded-md border bg-rose-50 border-rose-200 text-rose-800 text-xs" data-testid="account-overdue-banner">
                  <p className="font-semibold">⚠ OVERDUE — {accountInfo.account_name}</p>
                  {user?.role === 'super_admin' && accountInfo.current_balance != null && (
                    <p className="mt-0.5">
                      Balance: <strong>${(accountInfo.current_balance || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</strong>
                      {accountInfo.overdue_amount > 0 && (
                        <span className="text-rose-600 ml-1">(overdue ${accountInfo.overdue_amount.toLocaleString('en-AU', { minimumFractionDigits: 2 })})</span>
                      )}
                    </p>
                  )}
                  <p className="font-medium mt-0.5">Do not release vehicle until previous statement is cleared.</p>
                </div>
              )}
            </div>

            {/* ───── COL 2 — Vehicle Details ───── */}
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">Vehicle Details</h3>
                {invoice.vehicle_id && (
                  <button
                    type="button"
                    onClick={openEditVehicle}
                    className="text-[11px] font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    data-testid="edit-vehicle-details-btn"
                  >
                    Edit
                  </button>
                )}
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-slate-500">Make &amp; Model</Label>
                <Select
                  value={invoice.vehicle_id || 'none'}
                  onValueChange={(val) => selectVehicle(val === 'none' ? '' : val)}
                >
                  <SelectTrigger className="h-9" data-testid="invoice-vehicle-select">
                    <SelectValue placeholder="Select vehicle..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No vehicle</SelectItem>
                    {customerVehicles.map(v => (
                      <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                        {v.rego} - {v.make} {v.model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                  <Car className="h-3 w-3" /> Rego
                </Label>
                <Input
                  value={invoice.vehicle_rego || ''}
                  readOnly
                  className="bg-slate-50 h-9 font-mono font-bold tracking-wider"
                  placeholder="—"
                  data-testid="invoice-vehicle-rego"
                />
              </div>
              <div>
                <Label htmlFor="odometer" className="text-[11px] uppercase tracking-wide text-slate-500">Odometer (km)</Label>
                <Input
                  id="odometer"
                  type="number"
                  value={invoice.odometer_reading || ''}
                  onChange={(e) => updateField('odometer_reading', e.target.value)}
                  placeholder="e.g. 120000"
                  className="h-9"
                  data-testid="invoice-odometer"
                />
              </div>
              <div>
                <Label htmlFor="vin" className="text-[11px] uppercase tracking-wide text-slate-500">VIN</Label>
                <Input
                  id="vin"
                  value={invoice.vin_number || ''}
                  onChange={(e) => updateField('vin_number', e.target.value)}
                  placeholder="17-char VIN"
                  className="h-9 font-mono text-sm"
                  data-testid="invoice-vin"
                />
              </div>
            </div>

            {/* ───── COL 3 — Invoice Summary ───── */}
            <div className="space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">Invoice Summary</h3>
              <div>
                <Label htmlFor="invoice_date" className="text-[11px] uppercase tracking-wide text-slate-500">Document Date</Label>
                <Input
                  id="invoice_date"
                  type="date"
                  value={invoice.invoice_date || ''}
                  onChange={(e) => updateField('invoice_date', e.target.value)}
                  className="h-9"
                  data-testid="invoice-doc-date"
                />
              </div>
              <div>
                <Label htmlFor="doc_no" className="text-[11px] uppercase tracking-wide text-slate-500">Document Number</Label>
                <Input
                  id="doc_no"
                  value={invoice.doc_no || 'Auto-generated'}
                  disabled
                  className="bg-slate-50 h-9 font-mono"
                  data-testid="invoice-doc-no"
                />
              </div>
              <div>
                <Label htmlFor="service_date" className="text-[11px] uppercase tracking-wide text-slate-500">Service Date</Label>
                <Input
                  id="service_date"
                  type="date"
                  value={invoice.service_date || ''}
                  onChange={(e) => updateField('service_date', e.target.value)}
                  className="h-9"
                  data-testid="invoice-service-date"
                />
              </div>
              <div>
                <Label htmlFor="next_service_date" className="text-[11px] uppercase tracking-wide text-slate-500">Next Service Date</Label>
                <Input
                  id="next_service_date"
                  type="date"
                  value={invoice.next_service_date || ''}
                  onChange={(e) => updateField('next_service_date', e.target.value)}
                  className="h-9"
                  data-testid="invoice-next-service-date"
                />
              </div>
            </div>

            {/* ───── COL 4 — Work Details ───── */}
            <div className="space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">Work Details</h3>
              <div>
                <Label htmlFor="service_interval" className="text-[11px] uppercase tracking-wide text-slate-500">Service Interval</Label>
                <Input
                  id="service_interval"
                  value={invoice.service_interval || ''}
                  onChange={(e) => updateField('service_interval', e.target.value)}
                  placeholder="e.g. 20,000km / 24 months"
                  className="h-9"
                  data-testid="invoice-service-interval"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-slate-500">Main Category</Label>
                <ServiceCategoriesPicker
                  value={invoice.service_categories || []}
                  onChange={(next) => updateField('service_categories', next)}
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-slate-500">Account Type</Label>
                <AccountTypeBadge accountInfo={accountInfo} />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b">
            <Button variant="outline" size="sm" onClick={() => addNewLine('part')}>
              <Plus className="h-4 w-4 mr-1" />
              New Line
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setShowPartsDialog(true); fetchPartsCatalog(); }}
              data-testid="from-catalog-btn"
            >
              <Package className="h-4 w-4 mr-1" />
              From Catalog
            </Button>
            <Button 
              variant="default" 
              size="sm" 
              onClick={() => { setShowCustomItemsDialog(true); fetchInvoiceTemplates(); }}
              className="bg-green-600 hover:bg-green-700"
              data-testid="add-custom-item-btn"
            >
              <FileText className="h-4 w-4 mr-1" />
              Add Custom Item
            </Button>
            <Button variant="outline" size="sm" onClick={() => addNewLine('labour')}>
              <Clock className="h-4 w-4 mr-1" />
              Add Labour
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddSupplier(true)}
              data-testid="add-supplier-btn"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              title="Register a new supplier — instantly available in every Supplier dropdown."
            >
              <Truck className="h-4 w-4 mr-1" />
              Add Supplier
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => addNewLine('tyre')}
              data-testid="add-tyre-btn"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              {/* Tyre = special pricing row: ORP × 1.30 + flat fitting/balancing fee per tyre */}
              <Package className="h-4 w-4 mr-1" />
              Add Tyre
            </Button>
            <Button variant="outline" size="sm" onClick={() => addNewLine('service')}>
              <Wrench className="h-4 w-4 mr-1" />
              Add Service
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setShowTemplatesDialog(true); fetchTemplates(); }}
              data-testid="apply-template-btn"
            >
              <Settings2 className="h-4 w-4 mr-1" />
              Note Templates
            </Button>

            <p className="basis-full text-[11px] text-slate-500 italic mt-1" data-testid="line-item-hint">
              All structured lines need <b>Description · Supplier · RRP · Margin · Labour hrs × rate</b> before save. ORP (supplier cost) is optional — fill it in when the supplier bill arrives.
            </p>
            
            <div className="ml-auto flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Label htmlFor="gst_toggle" className="text-sm">GST Inclusive</Label>
                <Switch
                  id="gst_toggle"
                  checked={invoice.gst_inclusive}
                  onCheckedChange={(checked) => updateField('gst_inclusive', checked)}
                />
              </div>
              {(user?.role === 'super_admin' || ['service_manager', 'floor_manager'].includes(user?.role)) && (
                <div className="flex items-center gap-1" data-testid="line-expand-controls">
                  {/* Replaces the old global "Show cost breakdown" switch
                      with a per-line expand pattern. SM rarely needs all
                      rows open at once — most rows look like the printed
                      invoice; click a chevron when you need to adjust a
                      specific line's cost/margin. */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-slate-500 hover:text-slate-900"
                    onClick={expandAllLines}
                    data-testid="expand-all-lines"
                    title="Expand every line's cost breakdown"
                  >
                    <ChevronDown className="h-3.5 w-3.5 mr-1" /> Expand all
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-slate-500 hover:text-slate-900"
                    onClick={collapseAllLines}
                    data-testid="collapse-all-lines"
                    title="Hide all cost breakdowns — table looks like the printable PDF"
                  >
                    <ChevronRight className="h-3.5 w-3.5 mr-1" /> Collapse all
                  </Button>
                </div>
              )}
              {(user?.role === 'super_admin' || ['service_manager', 'floor_manager'].includes(user?.role)) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={`h-8 px-2 text-xs gap-1.5 ${showOrp ? 'border-rose-300 text-rose-700 bg-rose-50' : 'border-slate-300 text-slate-600'}`}
                  onClick={() => setShowOrp(v => !v)}
                  data-testid="orp-privacy-toggle"
                  title={showOrp
                    ? 'Hide ORP — safe to show customer'
                    : 'Reveal supplier cost (ORP). Hide before turning the screen toward a customer.'}
                >
                  {showOrp ? (
                    <>
                      <EyeOff className="h-3.5 w-3.5" /> Hide ORP
                    </>
                  ) : (
                    <>
                      <Eye className="h-3.5 w-3.5" /> Show ORP
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Line Items Table */}
          <div className="overflow-x-auto mb-6">
            <DndContext
              sensors={dndSensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => {
                const { active, over } = event;
                if (active?.id && over?.id) reorderLineItems(active.id, over.id);
              }}
            >
            <SortableContext
              items={invoice.line_items.map(l => l.line_id)}
              strategy={verticalListSortingStrategy}
            >
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-8"></TableHead>
                  <TableHead className="min-w-[280px]">Description</TableHead>
                  <TableHead className="w-32">Part Number</TableHead>
                  <TableHead className="w-20 text-right">Qty</TableHead>
                  <TableHead className="w-24">Unit</TableHead>
                  <TableHead className="w-28 text-right">Unit Price</TableHead>
                  <TableHead className="w-32 text-right">
                    Total {invoice.gst_inclusive ? '(inc GST)' : '(ex GST)'}
                  </TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoice.line_items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      No line items. Click New Line to add items.
                    </TableCell>
                  </TableRow>
                ) : (
                  invoice.line_items.map((item, index) => {
                    const cat = item.category || 'simple';
                    const isStructured = cat !== 'simple';
                    // Margin floor removed (SM request, Aug 2026) — margin is
                    // fully flexible, so a line is never flagged "invalid" for margin.
                    const marginInvalid = false;
                    // Decide whether this line needs SM attention. A
                    // collapsed row that's missing required structured
                    // fields gets a small red dot on the chevron so the
                    // SM knows to expand. We only enforce this for the
                    // categories that actually require those fields.
                    const needsParts = cat === 'part' || cat === 'part_and_labour';
                    const needsLabour = cat === 'labour' || cat === 'part_and_labour';
                    const partsMissing = needsParts && (
                      item.parts_cost_price == null || item.parts_cost_price === '' ||
                      item.parts_price == null || item.parts_price === '' ||
                      !item.parts_supplier
                    );
                    const labourMissing = needsLabour && (
                      item.labour_hours == null || item.labour_hours === ''
                    );
                    const lineIncomplete = isStructured && (partsMissing || labourMissing || marginInvalid);
                    const isExpanded = expandedLineIds.has(item.line_id) || lineIncomplete;
                    return (
                    <SortableInvoiceRow key={item.line_id} id={item.line_id}>
                    {({ setNodeRef, attributes, listeners, style, isDragging }) => (
                    <React.Fragment>
                    <TableRow
                      ref={setNodeRef}
                      style={style}
                      className={`hover:bg-slate-50 ${isExpanded ? 'bg-slate-50/40' : ''} ${isDragging ? 'shadow-lg ring-2 ring-blue-200' : ''}`}
                      data-testid={`line-row-${index}`}
                    >
                      <TableCell className="px-1">
                        <div className="flex items-center gap-0.5">
                          {/* iter138.9e — 6-dot drag handle. Mouse OR
                              iPad touch (long-press 150 ms). The whole
                              row reorders when dropped. */}
                          <button
                            type="button"
                            {...attributes}
                            {...listeners}
                            className="inline-flex items-center justify-center min-h-[36px] min-w-[28px] rounded text-slate-300 hover:text-slate-600 hover:bg-slate-100 active:bg-slate-200 cursor-grab active:cursor-grabbing touch-none select-none"
                            aria-label="Drag to reorder line item"
                            title="Drag to reorder"
                            data-testid={`line-drag-${index}`}
                          >
                            <GripVertical className="h-4 w-4" />
                          </button>
                          {/* Click-to-expand chevron. */}
                          <button
                            type="button"
                            onClick={() => toggleLineExpand(item.line_id)}
                            className="relative inline-flex items-center justify-center h-7 w-7 rounded hover:bg-slate-200 text-slate-500"
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                            data-testid={`line-expand-${index}`}
                            title={isExpanded ? 'Collapse' : 'Expand for ORP / RRP / Margin / Labour'}
                          >
                            {isExpanded
                              ? <ChevronDown className="h-4 w-4" />
                              : <ChevronRight className="h-4 w-4" />}
                            {/* Red dot when collapsed AND incomplete. */}
                            {!expandedLineIds.has(item.line_id) && lineIncomplete && (
                              <span
                                className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white"
                                aria-hidden="true"
                                data-testid={`line-incomplete-dot-${index}`}
                                title="Required fields missing — click to expand"
                              />
                            )}
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={item.description || ''}
                          onChange={(e) => updateLineItem(item.line_id, 'description', e.target.value.toUpperCase())}
                          placeholder="ENTER DESCRIPTION..."
                          className="border-0 bg-transparent p-0 h-8 focus-visible:ring-0 uppercase"
                          data-testid={`invoice-line-desc-${item.line_id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={item.part_number || ''}
                          onChange={(e) => updateLineItem(item.line_id, 'part_number', e.target.value.toUpperCase())}
                          placeholder="-"
                          className="border-0 bg-transparent p-0 h-8 focus-visible:ring-0 font-mono text-xs uppercase"
                        />
                      </TableCell>
                      <TableCell>
                        {isStructured ? (
                          <div
                            className="text-right tabular-nums text-slate-700 pr-1 h-8 flex items-center justify-end"
                            data-testid={`line-qty-display-${index}`}
                            title="Set quantity in the expanded details (next to Margin)"
                          >
                            {item.quantity !== undefined && item.quantity !== null && item.quantity !== '' ? item.quantity : 1}
                          </div>
                        ) : (
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.quantity !== undefined && item.quantity !== null ? item.quantity : ''}
                            onChange={(e) => updateLineItem(item.line_id, 'quantity', e.target.value)}
                            className="border-0 bg-transparent p-0 h-8 text-right focus-visible:ring-0 w-16"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={item.unit || 'each'} 
                          onValueChange={(val) => updateLineItem(item.line_id, 'unit', val)}
                        >
                          <SelectTrigger className="border-0 bg-transparent h-8 p-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {UNIT_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {isStructured ? (
                          <div className="text-right font-medium text-slate-700 tabular-nums">
                            ${parseFloat(item.unit_price || 0).toFixed(2)}
                          </div>
                        ) : (
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price !== undefined && item.unit_price !== null ? item.unit_price : ''}
                            onChange={(e) => updateLineItem(item.line_id, 'unit_price', e.target.value)}
                            className="border-0 bg-transparent p-0 h-8 text-right focus-visible:ring-0 w-24"
                          />
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        ${parseFloat(item.line_total || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => deleteLineItem(item.line_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    {/* Structured breakdown panel — visible when the row
                        is expanded (manual click OR auto-expanded because
                        a required field is missing). Contains Category
                        selector, ORP/RRP/Margin/Supplier, and Labour
                        rate/hours. Hidden by default to keep the table
                        looking like the printable customer PDF. */}
                    {isExpanded && (
                      <TableRow className="bg-slate-50/70 border-b border-slate-200" data-testid={`line-breakdown-${index}`}>
                        <TableCell></TableCell>
                        <TableCell colSpan={7} className="py-3">
                          <div className="space-y-3">
                            {/* Top row: Category dropdown + drag handle. */}
                            <div className="flex items-center gap-3">
                              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Category:</span>
                              <Select
                                value={cat}
                                onValueChange={(val) => updateLineItem(item.line_id, 'category', val)}
                              >
                                <SelectTrigger className="h-7 text-xs w-44" data-testid={`line-category-${index}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {cat === 'simple' && (
                                    <SelectItem value="simple">Simple (legacy)</SelectItem>
                                  )}
                                  <SelectItem value="part_and_labour">Part &amp; Labour</SelectItem>
                                  <SelectItem value="part">Part only</SelectItem>
                                  <SelectItem value="labour">Labour only</SelectItem>
                                  <SelectItem value="tyre">Tyre (ORP + margin + flat)</SelectItem>
                                </SelectContent>
                              </Select>
                              <div className="ml-auto flex items-center gap-2 text-slate-400">
                                <span className="text-[10px] uppercase tracking-wide text-slate-400 hidden sm:inline">Order</span>
                                <span
                                  className="inline-flex items-center justify-center h-7 min-w-[28px] px-1.5 rounded text-[10px] font-bold bg-slate-200 text-slate-600 tabular-nums"
                                  title="Position in invoice — drag the 6-dot handle on the row to reorder"
                                  data-testid={`line-position-${index}`}
                                >
                                  {index + 1}
                                </span>
                              </div>
                            </div>

                            {/* Parts breakdown (also covers tyres — only
                                the labour row below is omitted for tyres). */}
                            {(cat === 'part_and_labour' || cat === 'part' || cat === 'tyre') && (
                              <div className="flex flex-wrap items-center gap-4 text-xs pl-1">
                                {showOrp && (
                                  <label className="flex items-center gap-1.5" data-testid={`line-cost-wrapper-${index}`}>
                                    <span className="text-rose-600 font-medium">ORP $ (cost)</span>
                                    <Input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={item.parts_cost_price ?? ''}
                                      onChange={(e) => updateLineItem(item.line_id, 'parts_cost_price', e.target.value)}
                                      placeholder="supplier"
                                      className="h-7 w-20 text-right text-xs border-rose-200 focus-visible:ring-rose-200"
                                      data-testid={`line-parts-cost-${index}`}
                                    />
                                  </label>
                                )}
                                <label className="flex items-center gap-1.5">
                                  <span className="text-slate-500">Supplier</span>
                                  <div className="w-32">
                                    <SupplierCombobox
                                      value={item.parts_supplier ?? ''}
                                      onChange={(v) => updateLineItem(item.line_id, 'parts_supplier', v)}
                                      testId={`line-supplier-${index}`}
                                      size="xs"
                                    />
                                  </div>
                                </label>
                                {cat !== 'tyre' && (
                                  <label className="flex items-center gap-1.5">
                                    <span className="text-slate-500">RRP $ (incl GST)</span>
                                    <Input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={item.parts_price ?? ''}
                                      onChange={(e) => updateLineItem(item.line_id, 'parts_price', e.target.value)}
                                      className="h-7 w-20 text-right text-xs"
                                      data-testid={`line-parts-price-${index}`}
                                    />
                                  </label>
                                )}
                                <label className="flex items-center gap-1.5">
                                  <span className="text-slate-500">Margin %</span>
                                  <Input
                                    type="number"
                                    min={0}
                                    max="200"
                                    step="0.5"
                                    value={item.parts_margin_pct ?? (cat === 'tyre' ? 30 : workshopDefaultMargin)}
                                    onChange={(e) => updateLineItem(item.line_id, 'parts_margin_pct', e.target.value)}
                                    placeholder={cat === 'tyre' ? 'manual %' : undefined}
                                    title={cat === 'tyre' ? 'Tyre margin is manual — you type it. Blank = 0%. Flat-rate covers fitting/balancing/valve/disposal.' : undefined}
                                    className={`h-7 w-20 text-right text-xs ${marginInvalid ? 'border-red-500 text-red-600' : ''}`}
                                    data-testid={`line-margin-${index}`}
                                  />
                                  {marginInvalid && (
                                    <span className="text-red-600 text-[10px]">min 40%</span>
                                  )}
                                </label>
                                {/* Qty moved here (SM request Aug-2026) — sits
                                    with the parts-charge group, out of the main
                                    row. Parts scale with qty; labour is flat. */}
                                <label className="flex items-center gap-1.5">
                                  <span className="text-slate-500">Qty</span>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={item.quantity !== undefined && item.quantity !== null ? item.quantity : ''}
                                    onChange={(e) => updateLineItem(item.line_id, 'quantity', e.target.value)}
                                    className="h-7 w-20 text-right text-xs"
                                    data-testid={`line-qty-${index}`}
                                  />
                                </label>
                                {cat === 'tyre' && (
                                  <label className="flex items-center gap-1.5" data-testid={`line-tyre-flat-wrapper-${index}`}>
                                    <span className="text-amber-700 font-medium">Flat $ (per tyre)</span>
                                    <Input
                                      type="number"
                                      min="0"
                                      step="0.50"
                                      value={item.tyre_flat_rate ?? ''}
                                      onChange={(e) => updateLineItem(item.line_id, 'tyre_flat_rate', e.target.value)}
                                      placeholder="fit+bal+valve"
                                      className="h-7 w-20 text-right text-xs border-amber-200 focus-visible:ring-amber-200"
                                      data-testid={`line-tyre-flat-${index}`}
                                      title="Per-tyre fitting + balancing + valve + disposal — adds to ORP × (1 + margin%)"
                                    />
                                  </label>
                                )}
                                <span className="text-emerald-700 font-medium">
                                  ≈ {cat === 'tyre' ? 'Per tyre' : 'Parts charge'} ${
                                    cat === 'tyre'
                                      ? ((item.parts_charge ?? 0) + (Number(item.tyre_flat_rate) || 0)).toFixed(2)
                                      : (item.parts_charge ?? 0).toFixed(2)
                                  }
                                </span>
                                {showOrp && item.parts_cost_price > 0 && item.parts_price > 0 && (
                                  (() => {
                                    const c = Number(item.parts_cost_price);
                                    const r = Number(item.parts_price);
                                    const m = ((r - c) / c) * 100;
                                    const tone = m < 0 ? 'text-rose-700 bg-rose-50 border-rose-200'
                                      : m < 40 ? 'text-amber-700 bg-amber-50 border-amber-200'
                                      : 'text-emerald-700 bg-emerald-50 border-emerald-200';
                                    return (
                                      <span
                                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${tone}`}
                                        title="(RRP − ORP) / ORP × 100"
                                        data-testid={`line-cost-margin-${index}`}
                                      >
                                        {m >= 0 ? '+' : ''}{m.toFixed(1)}% from cost
                                      </span>
                                    );
                                  })()
                                )}
                              </div>
                            )}

                            {/* Labour breakdown */}
                            {(cat === 'part_and_labour' || cat === 'labour') && (
                              <div className="flex flex-wrap items-center gap-4 text-xs pl-1">
                                <label className="flex items-center gap-1.5">
                                  <span className="text-slate-500">Labour $/hr</span>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    value={item.labour_rate ?? workshopLabourRate}
                                    onChange={(e) => updateLineItem(item.line_id, 'labour_rate', e.target.value)}
                                    className="h-7 w-20 text-right text-xs"
                                    data-testid={`line-labour-rate-${index}`}
                                  />
                                </label>
                                <label className="flex items-center gap-1.5">
                                  <span className="text-slate-500">Hours</span>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.25"
                                    value={item.labour_hours ?? ''}
                                    onChange={(e) => updateLineItem(item.line_id, 'labour_hours', e.target.value)}
                                    className="h-7 w-20 text-right text-xs"
                                    data-testid={`line-labour-hours-${index}`}
                                  />
                                </label>
                                {cat === 'labour' && (
                                  <label className="flex items-center gap-1.5">
                                    <span className="text-slate-500">Qty</span>
                                    <Input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={item.quantity !== undefined && item.quantity !== null ? item.quantity : ''}
                                      onChange={(e) => updateLineItem(item.line_id, 'quantity', e.target.value)}
                                      className="h-7 w-20 text-right text-xs"
                                      data-testid={`line-qty-${index}`}
                                      title="Labour is charged once for the whole job — qty does not multiply it."
                                    />
                                  </label>
                                )}
                                <span className="text-blue-700 font-medium">
                                  ≈ Labour charge ${(item.labour_charge ?? 0).toFixed(2)}
                                </span>
                                <span className="ml-auto text-slate-700 font-semibold">
                                  Unit price = ${parseFloat(item.unit_price || 0).toFixed(2)}
                                </span>
                              </div>
                            )}

                            {/* When ONLY parts (no labour row above), pin the
                                unit price summary at the bottom right too. */}
                            {cat === 'part' && (
                              <div className="flex justify-end text-xs">
                                <span className="text-slate-700 font-semibold">
                                  Unit price = ${parseFloat(item.unit_price || 0).toFixed(2)}
                                </span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                    )}
                    </SortableInvoiceRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            </SortableContext>
            </DndContext>
          </div>

          {/* Totals Section */}
          <div className="flex justify-end">
            <div className="w-80 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal:</span>
                <span className="font-medium">${totals.subtotal.toFixed(2)}</span>
              </div>
              
              <div className="flex items-center justify-between gap-2">
                <span className="text-slate-600 text-sm">Discount:</span>
                <div className="flex items-center gap-2">
                  <Select 
                    value={invoice.discount_type} 
                    onValueChange={(val) => updateField('discount_type', val)}
                  >
                    <SelectTrigger className="w-16 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dollar">$</SelectItem>
                      <SelectItem value="percent">%</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={invoice.discount_value || ''}
                    onChange={(e) => updateField('discount_value', e.target.value)}
                    className="w-24 h-8 text-right"
                  />
                  <span className="w-20 text-right font-medium">
                    -${totals.discount_total.toFixed(2)}
                  </span>
                </div>
              </div>
              
              <div className="flex justify-between text-sm pt-2 border-t">
                <span className="text-slate-600">
                  Total {invoice.gst_inclusive ? '(inc GST)' : '(ex GST)'}:
                </span>
                <span className="font-bold text-lg">${totals.total.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between text-sm text-slate-500">
                <span>GST ({invoice.gst_rate}%):</span>
                <span>${totals.gst_total.toFixed(2)}</span>
              </div>
              
              {/* iter123: Highlight Amount Paid (the default-to-total
                  field) instead of Amount Due. Workshop ops norm is
                  "paid at collection" so Amount Paid being the focus
                  matches the day-to-day SM flow. Amount Due only turns
                  red when there's actually a balance owing. */}
              <div className="flex items-center justify-between gap-2 pt-2 border-t bg-emerald-50/50 -mx-2 px-2 py-2 rounded-lg">
                <div>
                  <span className="text-emerald-800 text-sm font-semibold">Amount Paid:</span>
                  <p className="text-[10px] text-emerald-600/80 italic">Defaults to Total · edit if customer underpaid</p>
                </div>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={invoice.amount_paid || ''}
                  onChange={(e) => {
                    updateField('amount_paid', e.target.value);
                  }}
                  className="w-32 h-9 text-right font-bold text-emerald-700 border-emerald-300 focus:ring-emerald-400"
                  data-testid="invoice-amount-paid-input"
                />
              </div>

              {totals.amount_due > 0 ? (
                <div className="flex justify-between text-base font-bold pt-2 border-t border-rose-300">
                  <span className="text-rose-700">Amount Due:</span>
                  <span className="text-rose-700" data-testid="invoice-amount-due-display">
                    ${totals.amount_due.toFixed(2)}
                  </span>
                </div>
              ) : (
                <div className="flex justify-between text-xs pt-2 border-t text-slate-400">
                  <span>Amount Due:</span>
                  <span data-testid="invoice-amount-due-display">$0.00 — paid in full</span>
                </div>
              )}
            </div>
          </div>

          {/* Notes - opens in a popup */}
          <div className="mt-6 pt-6 border-t">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <Label className="text-sm font-semibold">Service Notes & Quote</Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  {(() => {
                    // Only count rows that are NOT soft-deleted — deleted rows
                    // stay on the document so SM can restore them from inside
                    // the modal, but they should NOT inflate the visible count
                    // or surface in the preview block below.
                    const visibleNotes = (invoice.notes_items || []).filter(r => !r.removed_at);
                    const visibleCount = visibleNotes.length;
                    const hasComments = invoice.notes && invoice.notes.replace(/<[^>]+>/g, '').trim();
                    if (visibleCount > 0 || hasComments) {
                      return `${visibleCount} quote row${visibleCount === 1 ? '' : 's'} · ${invoice.notes ? 'has customer comments' : 'no free-text comments yet'}`;
                    }
                    return 'Write comments and build the quote in a single pop-up.';
                  })()}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowServiceNotesModal(true)}
                data-testid="open-service-notes-modal"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                <FileText className="h-4 w-4 mr-2" />
                {((invoice.notes_items || []).filter(r => !r.removed_at).length) > 0 ? 'Edit Notes & Quote' : 'Open Service Notes'}
              </Button>
            </div>
            {/* Compact preview of approved rows — only LIVE (non-deleted) rows
                surface here. SM can still see and restore deleted rows from
                inside the modal via the "Restore Deleted" toggle. */}
            {(invoice.notes_items || []).filter(r => !r.removed_at).length > 0 && (
              <div className="mt-3 border border-slate-200 rounded-md bg-slate-50/60 divide-y divide-slate-200" data-testid="snm-preview">
                {(invoice.notes_items || []).filter(r => !r.removed_at).map(r => (
                  <div key={r.line_id} className="flex items-center gap-3 px-3 py-1.5 text-xs">
                    <span className={`h-2 w-2 rounded-full ${r.approved ? 'bg-emerald-500' : 'bg-slate-300'}`} title={r.approved ? 'Approved' : 'Not yet approved'}></span>
                    <span className="flex-1 truncate font-bold uppercase tracking-wide text-slate-800">{r.description}</span>
                    {r.added_to_job_card_at && (
                      <span className="text-[10px] text-emerald-700">✓ on JC</span>
                    )}
                    <span className="text-slate-500 tabular-nums">× {r.quantity ?? 1}</span>
                    <span className="font-semibold text-slate-900 tabular-nums w-16 text-right">${(Number(r.line_total) || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Service Notes Modal */}
        <ServiceNotesModal
          open={showServiceNotesModal}
          onOpenChange={setShowServiceNotesModal}
          invoice={invoice}
          invoiceId={invoiceId !== 'new' ? invoiceId : null}
          workshopRate={workshopLabourRate}
          onChangeNotes={(content) => updateField('notes', content)}
          onChangeNotesItems={(notes_items) => updateField('notes_items', notes_items)}
          onSaveInvoice={handleSave}
          onBookCustomer={(selectedRows) => {
            // iter138.9ao — Close service notes modal, open booking modal
            // pre-filled with this invoice's customer + vehicle and the
            // ticked quote rows as the booked tasks for mechanics.
            if (!invoice.customer_id) {
              toast.error('Set a customer on the invoice first');
              return;
            }
            setBookingFromQuote({
              prefill: {
                customer_id: invoice.customer_id,
                customer_name: invoice.customer_name || '',
                customer_phone: invoice.customer_phone || '',
                customer_email: invoice.customer_email || '',
                vehicle_id: invoice.vehicle_id || '',
                vehicle_rego: invoice.vehicle_rego || '',
                vehicle_make: invoice.vehicle_make || '',
                vehicle_model: invoice.vehicle_model || '',
                vehicle_year: invoice.vehicle_year || '',
              },
              prefillPendingItems: selectedRows,
            });
            setShowServiceNotesModal(false);
          }}
          onInvoiceUpdated={(suffix, data) => {
            // Server returns updated invoice fields. Always refresh notes_items
            // so the JC-stamping + clearing on bill is reflected immediately.
            setInvoice(prev => ({
              ...prev,
              line_items: data.line_items || prev.line_items,
              notes_items: data.notes_items !== undefined ? data.notes_items : prev.notes_items,
              subtotal: data.subtotal ?? prev.subtotal,
              gst_amount: data.gst_amount ?? prev.gst_amount,
              total: data.total ?? prev.total,
              amount_due: data.amount_due ?? prev.amount_due,
            }));
            // For add-to-job-card the response is summary-only — refetch the
            // invoice so we pick up the added_to_job_card_at stamps.
            if (suffix === 'add-approved-to-job-card' && invoiceId && invoiceId !== 'new') {
              fetch(`${BACKEND_URL}/api/invoices/${invoiceId}`, { credentials: 'include' })
                .then(r => r.ok ? r.json() : null)
                .then(fresh => {
                  if (fresh) {
                    setInvoice(prev => ({
                      ...prev,
                      notes_items: fresh.notes_items || prev.notes_items,
                    }));
                  }
                })
                .catch(() => {});
            }
          }}
        />

        {/* Add Supplier dialog — opened by the "Add Supplier" toolbar button.
            Newly-saved suppliers immediately bust the SupplierCombobox cache
            so every dropdown across the app picks them up without a page
            reload. */}
        <AddSupplierDialog
          open={showAddSupplier}
          onOpenChange={setShowAddSupplier}
        />

        {/* iter138.9ao — Book Customer from Quote Builder.
            Opened when SM ticks quote rows in ServiceNotesModal and clicks
            "Book Customer (N)". Pre-fills customer + vehicle + the ticked
            rows as pending-work items so they show as the tasks for
            mechanics on the new booking. */}
        <QuickBookingModal
          open={!!bookingFromQuote}
          onClose={() => setBookingFromQuote(null)}
          onCreated={(booking) => {
            toast.success(
              booking?.booking_number
                ? `Booking ${booking.booking_number} created from this quote`
                : 'Booking created from this quote',
            );
            setBookingFromQuote(null);
          }}
          getSiteParams={getSiteParams}
          prefill={bookingFromQuote?.prefill}
          prefillPendingItems={bookingFromQuote?.prefillPendingItems || null}
        />

        {/* Footer Actions */}
        <div className="flex items-center justify-between mt-6">
          <Button variant="outline" onClick={() => navigate('/invoices')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/invoices')}>
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
            {invoice.job_card_id && (
              <Button
                variant="outline"
                onClick={() => window.open(withSessionHash(`/job-cards?id=${invoice.job_card_id}`), '_blank')}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="header-go-to-jobcard"
                title="Open the linked job card in a new tab"
              >
                <Wrench className="h-4 w-4 mr-2" />
                Go to Job Card
              </Button>
            )}
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Invoice'}
            </Button>
          </div>
        </div>

        {/* Customer Search Dialog */}
        <Dialog open={showCustomerSearch} onOpenChange={setShowCustomerSearch}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Select Customer</DialogTitle>
              <DialogDescription>
                Search for a customer by name, phone, or email
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={customerSearchQuery}
                  onChange={(e) => {
                    setCustomerSearchQuery(e.target.value);
                    searchCustomers(e.target.value);
                  }}
                  placeholder="Search customers..."
                  className="pl-10"
                  autoFocus
                />
              </div>
              
              <div className="max-h-64 overflow-y-auto space-y-1">
                {customerResults.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">
                    {customerSearchQuery.length < 2 
                      ? 'Type at least 2 characters to search'
                      : 'No customers found'}
                  </p>
                ) : (
                  customerResults.map(customer => (
                    <button
                      key={customer.customer_id}
                      onClick={() => selectCustomer(customer)}
                      className="w-full p-3 text-left rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <div className="font-medium">
                        {customer.display_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim()}
                      </div>
                      <div className="text-sm text-slate-500">
                        {customer.mobile || customer.phone || customer.mobile_phone || customer.email || '-'}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCustomerSearch(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Parts Catalog Dialog */}
        <Dialog open={showPartsDialog} onOpenChange={setShowPartsDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Parts Catalog</DialogTitle>
              <DialogDescription>
                Search and add parts from your catalog, or create a new part
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={partsSearchQuery}
                    onChange={(e) => {
                      setPartsSearchQuery(e.target.value);
                      fetchPartsCatalog(e.target.value);
                    }}
                    placeholder="Search parts by name or part number..."
                    className="pl-10"
                    autoFocus
                    data-testid="parts-search-input"
                  />
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => setShowCreatePartDialog(true)}
                  data-testid="create-new-part-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  New Part
                </Button>
              </div>
              
              <div className="max-h-80 overflow-y-auto border rounded-lg">
                {loadingParts ? (
                  <div className="text-center py-8 text-slate-500">Loading parts...</div>
                ) : partsCatalog.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No parts found</p>
                    <Button 
                      variant="link" 
                      onClick={() => setShowCreatePartDialog(true)}
                      className="mt-2"
                    >
                      Create your first part
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Part Number</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                        <TableHead className="w-20"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {partsCatalog.map(part => (
                        <TableRow key={part.part_id} className="hover:bg-slate-50">
                          <TableCell className="font-mono text-sm">{part.part_number}</TableCell>
                          <TableCell>{part.description}</TableCell>
                          <TableCell className="text-right">${part.unit_price?.toFixed(2)}</TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => addPartFromCatalog(part)}
                              data-testid={`add-part-${part.part_id}`}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPartsDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Part Dialog */}
        <Dialog open={showCreatePartDialog} onOpenChange={setShowCreatePartDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Part</DialogTitle>
              <DialogDescription>
                Add a new part to your catalog and invoice
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Part Number *</Label>
                  <Input
                    value={newPart.part_number}
                    onChange={(e) => setNewPart({ ...newPart, part_number: e.target.value })}
                    placeholder="e.g., OIL-5W30"
                    data-testid="new-part-number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit Price *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newPart.unit_price}
                    onChange={(e) => setNewPart({ ...newPart, unit_price: e.target.value })}
                    placeholder="0.00"
                    data-testid="new-part-price"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Description *</Label>
                <Input
                  value={newPart.description}
                  onChange={(e) => setNewPart({ ...newPart, description: e.target.value })}
                  placeholder="Part description"
                  data-testid="new-part-description"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Unit</Label>
                  <Select 
                    value={newPart.unit} 
                    onValueChange={(val) => setNewPart({ ...newPart, unit: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UNIT_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select 
                    value={newPart.category} 
                    onValueChange={(val) => setNewPart({ ...newPart, category: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="oils">Oils & Fluids</SelectItem>
                      <SelectItem value="filters">Filters</SelectItem>
                      <SelectItem value="brakes">Brakes</SelectItem>
                      <SelectItem value="suspension">Suspension</SelectItem>
                      <SelectItem value="electrical">Electrical</SelectItem>
                      <SelectItem value="engine">Engine Parts</SelectItem>
                      <SelectItem value="tyres">Tyres</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreatePartDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreatePart} disabled={savingPart}>
                {savingPart ? 'Creating...' : 'Create & Add to Invoice'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Templates Dialog */}
        <Dialog open={showTemplatesDialog} onOpenChange={setShowTemplatesDialog}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Invoice Templates</DialogTitle>
              <DialogDescription>
                Apply pre-defined notes or full invoice templates
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowCreateTemplateDialog(true)}
                  data-testid="create-new-template-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  New Template
                </Button>
              </div>
              
              {loadingTemplates ? (
                <div className="text-center py-8 text-slate-500">Loading templates...</div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No templates found</p>
                  <Button 
                    variant="link" 
                    onClick={() => setShowCreateTemplateDialog(true)}
                    className="mt-2"
                  >
                    Create your first template
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {templates.map(template => (
                    <button
                      key={template.template_id}
                      onClick={() => applyTemplate(template)}
                      className="w-full p-4 text-left rounded-lg border hover:bg-slate-50 hover:border-blue-300 transition-colors"
                      data-testid={`template-${template.template_id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">{template.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {template.category === 'service_note' ? 'Note' : 'Full Template'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2">{template.content}</p>
                      {template.line_items?.length > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          {template.line_items.length} line item(s)
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTemplatesDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Template Dialog */}
        <Dialog open={showCreateTemplateDialog} onOpenChange={setShowCreateTemplateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create New Template</DialogTitle>
              <DialogDescription>
                Save current invoice line items as a reusable template
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  placeholder="e.g., Major Service Template"
                  data-testid="new-template-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Template Type</Label>
                <Select 
                  value={newTemplate.category} 
                  onValueChange={(val) => setNewTemplate({ ...newTemplate, category: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invoice_template">
                      Invoice Template (includes line items)
                    </SelectItem>
                    <SelectItem value="service_note">
                      Service Note (text only)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Notes / Description</Label>
                <Textarea
                  value={newTemplate.content}
                  onChange={(e) => setNewTemplate({ ...newTemplate, content: e.target.value })}
                  placeholder="Optional notes for this template..."
                  rows={3}
                />
              </div>
              
              {newTemplate.category === 'invoice_template' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-700 font-medium mb-1">
                    Line Items to Include <span className="text-[10px] font-normal text-blue-500">· ORP / RRP / Margin / Qty / Hrs / Rate all stored</span>
                  </p>
                  {invoice.line_items.length === 0 ? (
                    <p className="text-sm text-blue-600">
                      No line items in current invoice. Add items first.
                    </p>
                  ) : (
                    <ul className="text-xs text-blue-700 space-y-1.5">
                      {invoice.line_items.map((item, idx) => {
                        const cat = item.category || 'simple';
                        const bits = [];
                        if (item.parts_supplier) bits.push(`📦 ${item.parts_supplier}`);
                        if (cat !== 'simple' && cat !== 'labour') {
                          if (item.parts_cost_price != null) bits.push(`ORP $${Number(item.parts_cost_price).toFixed(2)}`);
                          if (item.parts_price != null) bits.push(`RRP $${Number(item.parts_price).toFixed(2)}`);
                          if (item.parts_margin_pct != null) bits.push(`${item.parts_margin_pct}% mrg`);
                        }
                        if (cat !== 'simple' && cat !== 'part') {
                          if (item.labour_hours) bits.push(`${item.labour_hours}h`);
                          if (item.labour_rate) bits.push(`@ $${item.labour_rate}/h`);
                        }
                        if (item.quantity != null && item.quantity !== 1) bits.push(`qty ${item.quantity}`);
                        return (
                          <li key={idx} data-testid={`tpl-create-preview-${idx}`} className="border-l-2 border-blue-300 pl-2">
                            <span className="font-medium">{item.description || 'Unnamed item'}</span>
                            <span className="ml-2 text-blue-500">· {cat}</span>
                            <span className="ml-2 text-blue-700">— ${parseFloat(item.line_total || item.unit_price || 0).toFixed(2)}</span>
                            {bits.length > 0 && (
                              <div className="text-[10px] text-blue-500 mt-0.5">
                                {bits.join(' · ')}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateTemplateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateTemplate} 
                disabled={savingTemplate || !newTemplate.name || (newTemplate.category === 'invoice_template' && invoice.line_items.length === 0)}
              >
                {savingTemplate ? 'Creating...' : 'Create Template'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Vehicle Details Modal — lets the SM update rego/make/model/
            year/VIN/colour/variant/odometer on the customer's vehicle so
            the invoice PDF + service history stay accurate. */}
        <Dialog open={showEditVehicle} onOpenChange={setShowEditVehicle}>
          <DialogContent className="sm:max-w-2xl" data-testid="edit-vehicle-dialog">
            <DialogHeader>
              <DialogTitle>Edit Vehicle Details</DialogTitle>
              <DialogDescription>
                Update the vehicle record — changes save to the vehicle
                database and refresh this invoice immediately.
              </DialogDescription>
            </DialogHeader>
            {loadingVehicleEdit ? (
              <div className="py-8 text-center text-sm text-slate-500">Loading…</div>
            ) : (
              <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="evd_rego">Rego *</Label>
                    <Input
                      id="evd_rego"
                      value={vehicleForm.rego}
                      onChange={e => setVehicleForm({ ...vehicleForm, rego: formatRego(e.target.value) })}
                      placeholder="ABC123"
                      className="font-mono"
                      data-testid="evd-rego"
                    />
                  </div>
                  <div>
                    <Label htmlFor="evd_year">Year</Label>
                    <Input
                      id="evd_year"
                      type="number"
                      value={vehicleForm.year}
                      onChange={e => setVehicleForm({ ...vehicleForm, year: e.target.value })}
                      placeholder="2020"
                      min="1900"
                      max="2099"
                      data-testid="evd-year"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="evd_make">Make</Label>
                    <Input
                      id="evd_make"
                      value={vehicleForm.make}
                      onChange={e => setVehicleForm({ ...vehicleForm, make: e.target.value })}
                      placeholder="Toyota"
                      data-testid="evd-make"
                    />
                  </div>
                  <div>
                    <Label htmlFor="evd_model">Model</Label>
                    <Input
                      id="evd_model"
                      value={vehicleForm.model}
                      onChange={e => setVehicleForm({ ...vehicleForm, model: e.target.value })}
                      placeholder="Camry"
                      data-testid="evd-model"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="evd_variant">Variant</Label>
                    <Input
                      id="evd_variant"
                      value={vehicleForm.variant}
                      onChange={e => setVehicleForm({ ...vehicleForm, variant: e.target.value })}
                      placeholder="Hybrid Ascent / SR5 / etc"
                      data-testid="evd-variant"
                    />
                  </div>
                  <div>
                    <Label htmlFor="evd_color">Colour</Label>
                    <Input
                      id="evd_color"
                      value={vehicleForm.color}
                      onChange={e => setVehicleForm({ ...vehicleForm, color: e.target.value })}
                      placeholder="Silver"
                      data-testid="evd-color"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="evd_vin">VIN</Label>
                  <Input
                    id="evd_vin"
                    value={vehicleForm.vin}
                    onChange={e => setVehicleForm({ ...vehicleForm, vin: e.target.value.toUpperCase() })}
                    placeholder="17-char VIN — used on PDF + state DB"
                    className="font-mono"
                    maxLength="17"
                    data-testid="evd-vin"
                  />
                </div>
                <div>
                  <Label htmlFor="evd_odometer">Odometer (km)</Label>
                  <Input
                    id="evd_odometer"
                    type="number"
                    value={vehicleForm.odometer}
                    onChange={e => setVehicleForm({ ...vehicleForm, odometer: e.target.value })}
                    placeholder="e.g. 120000"
                    min="0"
                    data-testid="evd-odometer"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    Service-history reading. Mechanic also captures odometer at SM Review.
                  </p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditVehicle(false)} disabled={savingVehicle}>
                Cancel
              </Button>
              <Button
                onClick={saveVehicleDetails}
                disabled={savingVehicle || loadingVehicleEdit}
                data-testid="evd-save"
              >
                {savingVehicle ? 'Saving…' : 'Save Vehicle'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Customer Details Modal — lets the SM update mobile/email/
            address etc. on the customer doc. Updates flow back to the
            customer DB AND the invoice snapshot. */}
        <Dialog open={showEditCustomer} onOpenChange={setShowEditCustomer}>
          <DialogContent className="sm:max-w-2xl" data-testid="edit-customer-dialog">
            <DialogHeader>
              <DialogTitle>Edit Customer Details</DialogTitle>
              <DialogDescription>
                Update the customer record — changes save to the customer
                database and refresh this invoice immediately.
              </DialogDescription>
            </DialogHeader>
            {loadingCustomerEdit ? (
              <div className="py-8 text-center text-sm text-slate-500">Loading…</div>
            ) : (
              <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="ecd_first_name">First name</Label>
                    <Input
                      id="ecd_first_name"
                      value={customerForm.first_name}
                      onChange={e => setCustomerForm({ ...customerForm, first_name: e.target.value })}
                      data-testid="ecd-first-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ecd_last_name">Last name</Label>
                    <Input
                      id="ecd_last_name"
                      value={customerForm.last_name}
                      onChange={e => setCustomerForm({ ...customerForm, last_name: e.target.value })}
                      data-testid="ecd-last-name"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="ecd_display_name">Display name (shown on invoice)</Label>
                  <Input
                    id="ecd_display_name"
                    value={customerForm.display_name}
                    onChange={e => setCustomerForm({ ...customerForm, display_name: e.target.value })}
                    placeholder="Auto-fills from First + Last if blank"
                    data-testid="ecd-display-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="ecd_mobile">Mobile</Label>
                    <Input
                      id="ecd_mobile"
                      value={customerForm.mobile}
                      onChange={e => setCustomerForm({ ...customerForm, mobile: formatAUMobile(e.target.value) })}
                      placeholder="04xx xxx xxx"
                      data-testid="ecd-mobile"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ecd_phone">Phone (landline)</Label>
                    <Input
                      id="ecd_phone"
                      value={customerForm.phone}
                      onChange={e => setCustomerForm({ ...customerForm, phone: formatAUMobile(e.target.value) })}
                      placeholder="Optional"
                      data-testid="ecd-phone"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="ecd_email">Email</Label>
                  <Input
                    id="ecd_email"
                    type="email"
                    value={customerForm.email}
                    onChange={e => setCustomerForm({ ...customerForm, email: e.target.value })}
                    placeholder="name@example.com"
                    data-testid="ecd-email"
                  />
                </div>
                <div className="border-t pt-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Address</p>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="ecd_address_line1">Address line 1</Label>
                      <Input
                        id="ecd_address_line1"
                        value={customerForm.address_line1}
                        onChange={e => setCustomerForm({ ...customerForm, address_line1: e.target.value })}
                        placeholder="Street address"
                        data-testid="ecd-address-line1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="ecd_address_line2">Address line 2</Label>
                      <Input
                        id="ecd_address_line2"
                        value={customerForm.address_line2}
                        onChange={e => setCustomerForm({ ...customerForm, address_line2: e.target.value })}
                        placeholder="Optional"
                        data-testid="ecd-address-line2"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor="ecd_suburb">Suburb</Label>
                        <Input
                          id="ecd_suburb"
                          value={customerForm.suburb}
                          onChange={e => setCustomerForm({ ...customerForm, suburb: e.target.value })}
                          data-testid="ecd-suburb"
                        />
                      </div>
                      <div>
                        <Label htmlFor="ecd_state">State</Label>
                        <Input
                          id="ecd_state"
                          value={customerForm.state}
                          onChange={e => setCustomerForm({ ...customerForm, state: e.target.value.toUpperCase() })}
                          placeholder="VIC"
                          data-testid="ecd-state"
                        />
                      </div>
                      <div>
                        <Label htmlFor="ecd_postcode">Postcode</Label>
                        <Input
                          id="ecd_postcode"
                          value={customerForm.postcode}
                          onChange={e => setCustomerForm({ ...customerForm, postcode: e.target.value })}
                          data-testid="ecd-postcode"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-t pt-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Business (optional)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="ecd_company">Company</Label>
                      <Input
                        id="ecd_company"
                        value={customerForm.company}
                        onChange={e => setCustomerForm({ ...customerForm, company: e.target.value })}
                        data-testid="ecd-company"
                      />
                    </div>
                    <div>
                      <Label htmlFor="ecd_abn">ABN</Label>
                      <Input
                        id="ecd_abn"
                        value={customerForm.abn}
                        onChange={e => setCustomerForm({ ...customerForm, abn: e.target.value })}
                        data-testid="ecd-abn"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditCustomer(false)} disabled={savingCustomer}>
                Cancel
              </Button>
              <Button
                onClick={saveCustomerDetails}
                disabled={savingCustomer || loadingCustomerEdit}
                data-testid="ecd-save"
              >
                {savingCustomer ? 'Saving…' : 'Save Customer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Email Template Modal (iter121) — same UX as SMS modal.
            SM picks an email template from the central library OR sends
            with the platform's default branded body. PDF attachment is
            handled by the backend. */}
        <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
          <DialogContent className="sm:max-w-lg" data-testid="invoice-email-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-purple-600" />
                Send Invoice via Email
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-slate-700">Sending to:</p>
                <p className="text-slate-600">{invoice.customer_email || invoice.customer_name}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Email Template</Label>
                <Select value={selectedEmailTemplate} onValueChange={setSelectedEmailTemplate}>
                  <SelectTrigger data-testid="invoice-email-template-select">
                    <SelectValue placeholder={emailTemplates.length === 0 ? 'No email templates yet — Super Admin → Templates → Email' : 'Default branded email (no template)'} />
                  </SelectTrigger>
                  <SelectContent>
                    {emailTemplates.map(t => (
                      <SelectItem
                        key={t.template_id}
                        value={t.template_id}
                        data-testid={`invoice-email-template-option-${t.template_id}`}
                      >
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500">
                  {selectedEmailTemplate
                    ? <>Subject &amp; body will be filled from this template. Placeholders like <code>{'{customer_name}'}</code>, <code>{'{vehicle_rego}'}</code>, <code>{'{invoice_total}'}</code>, <code>{'{doc_no}'}</code> are rendered server-side.</>
                    : 'Leave blank to send with the default branded email (subject = "Invoice {doc_no} from {shop_name}").'}
                </p>
              </div>

              {selectedEmailTemplate && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
                  <p className="text-[11px] uppercase tracking-wide text-purple-700 font-semibold mb-1">
                    {emailTemplates.find(t => t.template_id === selectedEmailTemplate)?.content?.subject || '— no subject —'}
                  </p>
                  <p className="text-purple-700 whitespace-pre-wrap text-xs">
                    {emailTemplates.find(t => t.template_id === selectedEmailTemplate)?.content?.body || ''}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setShowEmailModal(false); setSelectedEmailTemplate(''); }}
                disabled={sendingEmail}
                data-testid="invoice-email-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEmailInvoice}
                disabled={sendingEmail}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="invoice-email-send"
              >
                <Mail className="h-4 w-4 mr-2" />
                {sendingEmail ? 'Sending…' : (selectedEmailTemplate ? 'Send with template' : 'Send default email')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* SMS Template Modal */}
        <Dialog open={showSmsModal} onOpenChange={setShowSmsModal}>
          <DialogContent className="sm:max-w-lg" data-testid="invoice-sms-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                Send Invoice via SMS
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* iter123: multi-recipient picker — both mobile and
                  landline shown when present on the customer record.
                  SM ticks one or both; backend fans out the SMS. */}
              <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1.5">
                <p className="font-medium text-slate-700">Sending to: <span className="text-slate-900">{invoice.customer_name}</span></p>
                {(() => {
                  const mobile = customer?.mobile || invoice.customer_phone || '';
                  const landline = customer?.phone || invoice.customer_landline || '';
                  const sameNumber = mobile && landline && mobile === landline;
                  const showLandline = landline && !sameNumber;
                  if (!mobile && !landline) {
                    return <p className="text-rose-600 text-xs">⚠️ No phone numbers on file for this customer.</p>;
                  }
                  return (
                    <div className="space-y-1">
                      {mobile && (
                        <label className="flex items-center gap-2 cursor-pointer" data-testid="invoice-sms-target-mobile">
                          <input
                            type="checkbox"
                            checked={smsTargets.mobile}
                            onChange={(e) => setSmsTargets(t => ({ ...t, mobile: e.target.checked }))}
                            className="h-4 w-4"
                          />
                          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide w-20">Mobile</span>
                          <span className="font-mono text-xs text-slate-700">{mobile}</span>
                        </label>
                      )}
                      {showLandline && (
                        <label className="flex items-center gap-2 cursor-pointer" data-testid="invoice-sms-target-landline">
                          <input
                            type="checkbox"
                            checked={smsTargets.landline}
                            onChange={(e) => setSmsTargets(t => ({ ...t, landline: e.target.checked }))}
                            className="h-4 w-4"
                          />
                          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide w-20">Landline</span>
                          <span className="font-mono text-xs text-slate-700">{landline}</span>
                        </label>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Built-in QUOTE template — sends a tokenised PDF-review link
                  so the customer can tap once, see the full PDF on the
                  web, and Approve / Decline. SM doesn't have to compose
                  a custom message. */}
              <div
                className={`rounded-lg border p-3 transition-colors ${
                  quoteSmsMode
                    ? 'bg-emerald-50 border-emerald-300'
                    : 'bg-slate-50 border-slate-200 hover:border-emerald-200 cursor-pointer'
                }`}
                onClick={() => !quoteSmsMode && setQuoteSmsMode(true)}
                data-testid="quote-sms-card"
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={quoteSmsMode}
                    onChange={(e) => {
                      setQuoteSmsMode(e.target.checked);
                      if (e.target.checked) {
                        setSelectedSmsTemplate('');
                        setCustomSmsMessage('');
                        setQuoteSmsResult(null);
                      }
                    }}
                    data-testid="quote-sms-toggle"
                    className="mt-1 h-4 w-4"
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-emerald-900 text-sm">
                      Send Quote (PDF link) — recommended for new quotes
                    </p>
                    <p className="text-xs text-emerald-800/80 mt-1">
                      Customer gets a one-tap SMS link → reviews the FULL invoice PDF in their browser → taps <strong>Approve</strong> or <strong>Decline</strong>. You'll see their decision on the invoice list.
                    </p>
                  </div>
                </div>
                {quoteSmsResult && (
                  <div className="mt-3 p-2 bg-white border border-emerald-200 rounded text-xs">
                    <p className="font-semibold text-emerald-800 mb-1">✓ SMS sent. Link:</p>
                    <a href={quoteSmsResult.link} target="_blank" rel="noopener noreferrer"
                       className="text-blue-700 underline break-all" data-testid="quote-sms-link">
                      {quoteSmsResult.link}
                    </a>
                    <p className="mt-1 text-slate-600 break-words">
                      Preview: {quoteSmsResult.message_preview}
                    </p>
                  </div>
                )}
              </div>

              {!quoteSmsMode && (smsTemplates.length > 0 || (invoice.notes_items || []).filter(r => !r.removed_at).length > 0) && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Select Template</Label>
                  <Select value={selectedSmsTemplate} onValueChange={(val) => {
                    // iter141 — Special template option: "Quoted Items"
                    // pulls every Quote Builder line from Service Notes
                    // (invoice.notes_items) and formats it as an SMS body
                    // so SM can fire a quote-request to the customer for
                    // go-ahead approval BEFORE the work is done. Pulls
                    // strictly from notes_items (Quote Builder, not the
                    // already-sold invoice line_items).
                    if (val === '__quoted_items__') {
                      const notes = (invoice.notes_items || []).filter(r => !r.removed_at);
                      if (!notes.length) {
                        toast.error('No quoted items in Service Notes for this invoice');
                        setSelectedSmsTemplate('');
                        return;
                      }
                      let total = 0;
                      const lines = notes.map((r, i) => {
                        const lt = Number(r.line_total || 0);
                        total += lt;
                        const price = lt > 0 ? ` — $${lt.toFixed(2)}` : '';
                        return `${i + 1}. ${r.description || '(no description)'}${r.quantity && r.quantity > 1 ? ` × ${r.quantity}` : ''}${price}`;
                      }).join('\n');
                      const customerFirst = (invoice.customer_name || 'there').split(/\s+/)[0];
                      const rego = invoice.vehicle_rego ? ` (${invoice.vehicle_rego})` : '';
                      const body =
                        `Hi ${customerFirst}, here's the quote for your vehicle${rego}:\n\n` +
                        `${lines}\n\n` +
                        `Total: $${total.toFixed(2)}\n\n` +
                        `Reply YES to approve or call us back. Thanks!`;
                      setCustomSmsMessage(body);
                      setSelectedSmsTemplate('');  // route through "custom message" path on send
                      return;
                    }
                    setSelectedSmsTemplate(val);
                    setCustomSmsMessage('');
                  }}>
                    <SelectTrigger data-testid="invoice-sms-template-select">
                      <SelectValue placeholder="Choose a template..." />
                    </SelectTrigger>
                    <SelectContent>
                      {/* iter141 — Quoted Items from Service Notes (Quote Builder) */}
                      <SelectItem
                        value="__quoted_items__"
                        data-testid="invoice-sms-template-option-quoted-items"
                      >
                        Quoted Items (from Service Notes)
                      </SelectItem>
                      {smsTemplates.map(template => (
                        <SelectItem
                          key={template.template_id}
                          value={template.template_id}
                          data-testid={`invoice-sms-template-option-${template.template_id}`}
                        >
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedSmsTemplate && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                      <p className="text-blue-700">
                        {(() => {
                          const t = smsTemplates.find(t => t.template_id === selectedSmsTemplate);
                          // iter121: new template-library docs nest body under `content.body`; legacy stored a string.
                          const body = typeof t?.content === 'string' ? t.content : (t?.content?.body || '');
                          return body
                            .replace(/{customer_name}/g, invoice.customer_name || 'Customer')
                            .replace(/{vehicle_rego}/g, invoice.vehicle_rego || '')
                            .replace(/{invoice_total}/g, `$${totals.total.toFixed(2)}`)
                            .replace(/{invoice_id}/g, invoice.doc_no || '');
                        })()}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {!quoteSmsMode && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {smsTemplates.length > 0 ? 'Or write a custom message' : 'Message'}
                  </Label>
                  <Textarea
                    value={customSmsMessage}
                    onChange={(e) => {
                      setCustomSmsMessage(e.target.value);
                      setSelectedSmsTemplate('');
                    }}
                    placeholder="Type your message here..."
                    rows={4}
                    className="resize-none"
                    data-testid="invoice-sms-custom-message"
                  />
                  <p className="text-xs text-slate-500">
                    Available placeholders: {'{customer_name}'}, {'{vehicle_rego}'}, {'{invoice_total}'}, {'{invoice_id}'}
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowSmsModal(false); setQuoteSmsResult(null); setQuoteSmsMode(false); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendSms} 
                disabled={sendingSms || (!quoteSmsMode && !selectedSmsTemplate && !customSmsMessage.trim())}
                data-testid="invoice-send-sms-confirm-btn"
              >
                {sendingSms ? (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {quoteSmsMode ? 'Send Quote SMS' : 'Send SMS'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Custom Item Templates Dialog */}
        <Dialog open={showCustomItemsDialog} onOpenChange={setShowCustomItemsDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-green-600" />
                Add Custom Item Template
              </DialogTitle>
              <DialogDescription>
                Select a service package to add all its items to this invoice
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              {/* Search and Link to Templates */}
              <div className="flex items-center gap-2 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search templates..."
                    value={customItemSearchQuery}
                    onChange={(e) => setCustomItemSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="custom-item-search"
                  />
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setShowCustomItemsDialog(false);
                    navigate('/invoice-templates');
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Manage Templates
                </Button>
              </div>

              {/* Templates List */}
              {loadingInvoiceTemplates ? (
                <div className="text-center py-8 text-slate-500">Loading templates...</div>
              ) : filteredInvoiceTemplates.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-2">
                    {customItemSearchQuery ? 'No templates match your search' : 'No custom item templates yet'}
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setShowCustomItemsDialog(false);
                      navigate('/invoice-templates');
                    }}
                  >
                    Create Your First Template
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {filteredInvoiceTemplates.map(template => (
                    <div 
                      key={template.template_id}
                      className="border rounded-lg p-4 hover:border-green-300 hover:bg-green-50/50 cursor-pointer transition-colors"
                      onClick={() => applyCustomItemTemplate(template)}
                      data-testid={`template-${template.template_id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-slate-900">{template.name}</h4>
                          {template.content && (
                            <p className="text-sm text-slate-500 line-clamp-1">{template.content}</p>
                          )}
                        </div>
                        <Badge variant="secondary" className="ml-2">
                          {template.line_items?.length || 0} items
                        </Badge>
                      </div>
                      
                      {/* Preview items */}
                      <div className="bg-slate-50 rounded p-2 mt-2">
                        <div className="space-y-1 text-sm">
                          {(template.line_items || []).slice(0, 3).map((item, i) => (
                            <div key={i} className="flex justify-between text-slate-600">
                              <span className="truncate flex-1">{item.description}</span>
                              <span className="text-slate-500 ml-2 font-mono">
                                ${computeTemplateLineTotal(item).toFixed(2)}
                              </span>
                            </div>
                          ))}
                          {(template.line_items?.length || 0) > 3 && (
                            <p className="text-xs text-slate-400">
                              +{template.line_items.length - 3} more items...
                            </p>
                          )}
                        </div>
                        <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-200">
                          <span className="text-sm font-medium text-slate-700">Total:</span>
                          <span className="font-bold text-green-600">
                            ${(template.line_items || []).reduce((sum, item) => 
                              sum + computeTemplateLineTotal(item), 0
                            ).toFixed(2)}
                          </span>
                        </div>
                      </div>
                      
                      <p className="text-xs text-green-600 mt-2 font-medium">
                        Click to add all items to invoice
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCustomItemsDialog(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      </div>
      {commsDialog}
    </Layout>
  );
}
